// src/CostingSheet.js
import React, { useEffect, useState, useRef } from "react";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  query,
  orderBy,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "./firebase";
import { STATIC_EXCHANGE_RATES } from "./currencyUtils";
import "./styles.css";

import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Set pdfjs worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PDF_RENDER_WIDTH = 600;
const MIN_TEXT_LENGTH_FOR_AI = 50; // Minimum characters required for AI extraction
const MAX_ITEMS_FOR_AI_CALCULATION = 5; // Maximum items to analyze for AI duty/shipping calculations (to avoid excessive API costs)
const HSN_SUGGESTION_DELAY_MS = 500; // Delay before auto-suggesting HSN code after item name update
const CALCULATION_TRIGGER_DELAY_MS = 100; // Delay before triggering duty/shipping calculation after data update
const MIN_ITEM_NAME_LENGTH = 3; // Minimum item name length for HSN code suggestion

// Supported currencies
const SUPPORTED_CURRENCIES = [
  { code: "USD", name: "US Dollar", symbol: "$" },
  { code: "EUR", name: "Euro", symbol: "€" },
  { code: "GBP", name: "British Pound", symbol: "£" },
  { code: "INR", name: "Indian Rupee", symbol: "₹" },
  { code: "AED", name: "UAE Dirham", symbol: "د.إ" },
  { code: "SAR", name: "Saudi Riyal", symbol: "﷼" },
  { code: "JPY", name: "Japanese Yen", symbol: "¥" },
  { code: "CNY", name: "Chinese Yuan", symbol: "¥" },
  { code: "AUD", name: "Australian Dollar", symbol: "A$" },
  { code: "CAD", name: "Canadian Dollar", symbol: "C$" }
];

// Standard Incoterms (International Commercial Terms)
const DELIVERY_TERMS = [
  "EXW - Ex Works",
  "FCA - Free Carrier",
  "FAS - Free Alongside Ship",
  "FOB - Free On Board",
  "CFR - Cost and Freight",
  "CIF - Cost, Insurance and Freight",
  "CPT - Carriage Paid To",
  "CIP - Carriage and Insurance Paid To",
  "DAP - Delivered At Place",
  "DPU - Delivered at Place Unloaded",
  "DDP - Delivered Duty Paid"
];

// Incoterm Cost Responsibility Mapping
// Legend: "vendor" = vendor covers cost (set to 0), "you" = buyer covers cost (calculate/input)
const INCOTERM_RESPONSIBILITIES = {
  "DDP": {
    freight: "vendor",
    insurance: "vendor",
    portCharges: "vendor",
    customsDuty: "vendor",
    localDelivery: "vendor",
    description: "Delivered Duty Paid - Vendor covers all costs including customs"
  },
  "DAP": {
    freight: "vendor",
    insurance: "vendor",
    portCharges: "vendor",
    customsDuty: "you",
    localDelivery: "vendor",
    description: "Delivered At Place - You pay customs duty & VAT only"
  },
  "CIP": {
    freight: "vendor",
    insurance: "vendor",
    portCharges: "you",
    customsDuty: "you",
    localDelivery: "you",
    description: "Carriage and Insurance Paid To - You pay port, customs & local delivery"
  },
  "CIF": {
    freight: "vendor",
    insurance: "vendor",
    portCharges: "you",
    customsDuty: "you",
    localDelivery: "you",
    description: "Cost, Insurance and Freight - You pay port, customs & local delivery"
  },
  "CPT": {
    freight: "vendor",
    insurance: "you",
    portCharges: "you",
    customsDuty: "you",
    localDelivery: "you",
    description: "Carriage Paid To - You pay insurance, port, customs & local delivery"
  },
  "CFR": {
    freight: "vendor",
    insurance: "you",
    portCharges: "you",
    customsDuty: "you",
    localDelivery: "you",
    description: "Cost and Freight - You pay insurance, port, customs & local delivery"
  },
  "FOB": {
    freight: "you",
    insurance: "you",
    portCharges: "you",
    customsDuty: "you",
    localDelivery: "you",
    description: "Free On Board - You pay all costs from port of origin"
  },
  "FCA": {
    freight: "you",
    insurance: "you",
    portCharges: "you",
    customsDuty: "you",
    localDelivery: "you",
    description: "Free Carrier - You pay all costs from carrier pickup point"
  },
  "FAS": {
    freight: "you",
    insurance: "you",
    portCharges: "you",
    customsDuty: "you",
    localDelivery: "you",
    description: "Free Alongside Ship - You pay all costs from alongside ship"
  },
  "EXW": {
    freight: "you",
    insurance: "you",
    portCharges: "you",
    customsDuty: "you",
    localDelivery: "you",
    description: "Ex Works - You pay all costs from vendor's factory"
  },
  "DPU": {
    freight: "vendor",
    insurance: "vendor",
    portCharges: "vendor",
    customsDuty: "you",
    localDelivery: "vendor",
    description: "Delivered at Place Unloaded - You pay customs duty & VAT only"
  }
};

// Helper function to extract Incoterm code from delivery terms string
const extractIncotermCode = (deliveryTerms) => {
  if (!deliveryTerms) return null;
  const code = deliveryTerms.split(" - ")[0]?.trim();
  return code || null;
};

// Helper function to check if freight calculator should be enabled for an Incoterm
const isFreightCalculatorEnabled = (deliveryTerms) => {
  const incoterm = extractIncotermCode(deliveryTerms);
  if (!incoterm) return true; // Enable by default if no incoterm
  
  // Enable only for terms where buyer pays freight
  const enabledTerms = ["EXW", "FCA", "FAS", "FOB", "CFR", "CPT"];
  return enabledTerms.includes(incoterm);
};

// Helper function to get cost responsibilities for an Incoterm
const getIncotermResponsibilities = (deliveryTerms) => {
  const incoterm = extractIncotermCode(deliveryTerms);
  if (!incoterm || !INCOTERM_RESPONSIBILITIES[incoterm]) {
    return null; // No responsibilities if incoterm not found
  }
  return INCOTERM_RESPONSIBILITIES[incoterm];
};

// Common items that are frequently needed for projects
const COMMON_ITEMS = [
  "Internal Manpower (IMC) - Base Projects",
  "Internal Manpower (IMC) - Float Projects",
  "Internal Manpower (IMC) - Base Engineering",
  "Internal Manpower (IMC) - Float Engineering",
  "Advance Bank Guarantee (ABG)",
  "Performance Bank Guarantee (PBG)",
  "Insurance",
  "Vendor LC Cost",
  "Project LC Cost",
  "Risk Provision",
  "Cost of Capital",
  "Interest & Finance Costs",
  "Duty",
  "Outfreight",
  "Business Consultant",
  "LD Provision",
  "Credit Risk Insurance",
  "PE Provision"
];

// Cost categories for classification
const COST_CATEGORIES = [
  { id: "material", name: "Material", icon: "📦" },
  { id: "labor", name: "Labor", icon: "👷" },
  { id: "equipment", name: "Equipment", icon: "🔧" },
  { id: "freight", name: "Freight/Shipping", icon: "🚚" },
  { id: "tax", name: "Tax/Duty", icon: "🏛️" },
  { id: "insurance", name: "Insurance", icon: "🛡️" },
  { id: "finance", name: "Finance/Banking", icon: "🏦" },
  { id: "contingency", name: "Contingency", icon: "⚠️" },
  { id: "margin", name: "Margin/Profit", icon: "📈" },
  { id: "overhead", name: "Overhead/OPEX", icon: "🏢" },
  { id: "capex", name: "CAPEX", icon: "🏗️" },
  { id: "discount", name: "Discount", icon: "🏷️" },
  { id: "general", name: "General", icon: "📋" }
];

// Default margin/calculation rates (configurable)
const DEFAULT_CALCULATION_RATES = {
  leadTimeRisk: 5,              // 5% Lead Time Risk Factor
  currencyVolatility: 5,        // 5% Currency Volatility Buffer (AI-suggested)
  warrantyHandling: 3,          // 3% Warranty Handling Cost
  contingency: 5,               // 5% Contingency (AI-suggested)
  risk: 5,                      // 5% Risk (AI-suggested)
  businessAgent: 0,             // 0% Business Agent Commission
  cogs: 0,                      // 0% Cost of Goods Sold
  margin: 20,                   // 20% Margin
};

// Common costing mistakes to detect (used in AI Analysis)
// eslint-disable-next-line no-unused-vars
const COMMON_MISTAKES = [
  { id: "wrong_uom", name: "Wrong UoM Multiplier", description: "Unit of measure doesn't match quantity" },
  { id: "outdated_cost", name: "Outdated Material Cost", description: "Price significantly different from market rates" },
  { id: "qty_mismatch", name: "Quantity Mismatch", description: "Quantity differs from BOQ" },
  { id: "double_count", name: "Double Counting", description: "Same item appears multiple times" },
  { id: "copy_paste", name: "Copy-Paste Error", description: "Identical values suggesting copy-paste" },
  { id: "missing_item", name: "Missing Item", description: "Expected item not found in costing" },
  { id: "abnormal_price", name: "Abnormal Price", description: "Price significantly higher/lower than similar items" },
  { id: "zero_price", name: "Zero/Missing Price", description: "Item has no price assigned" }
];

/**
 * Extract text from a specific page of a PDF
 * @param {string} pdfUrl - URL of the PDF document
 * @param {number} pageNum - Page number to extract text from
 * @returns {Promise<string|null>} - Extracted text or null on error
 */
async function extractPageText(pdfUrl, pageNum) {
  try {
    const loadingTask = pdfjs.getDocument(pdfUrl);
    const pdf = await loadingTask.promise;

    if (pageNum < 1 || pageNum > pdf.numPages) return null;

    const page = await pdf.getPage(pageNum);
    const textContent = await page.getTextContent();
    const strings = textContent.items.map((item) => item.str);

    return `--- PAGE ${pageNum} ---\n\n` + strings.join(" ");
  } catch (error) {
    console.error("Error extracting page text:", error);
    return null;
  }
}

/**
 * CostingSheet Component with PDF Viewer
 * 
 * Features:
 * - Split-pane layout with PDF viewer and editable table
 * - Click-to-edit cells (inline editing)
 * - Select text from PDF and send to costing sheet
 * - AI extraction of BOQ items line by line
 * - Manual BOQ/costing sheet upload
 * - Auto-calculation of totals
 */
export default function CostingSheet({ tenderId, tender }) {
  const [costingItems, setCostingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [suggestingAccessories, setSuggestingAccessories] = useState(false);
  const [suggestingHSN, setSuggestingHSN] = useState({}); // Track which items are getting HSN suggestions
  
  // Subsystems for dropdown
  const [subsystems, setSubsystems] = useState([]);
  
  // Accessory suggestion modal state
  const [accessoryModal, setAccessoryModal] = useState({
    isOpen: false,
    itemName: "",
    accessories: [],
    selected: {}
  });
  
  // PDF states
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedDocUrl, setSelectedDocUrl] = useState("");
  const [selectedText, setSelectedText] = useState("");
  const [pdfMinimized, setPdfMinimized] = useState(false);
  
  // Drag-and-highlight states (same as compliance tab)
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragRect, setDragRect] = useState(null);
  const pageElementRef = useRef(null); // Cache PDF page element for performance
  
  // Inline editing state - track which cell is being edited
  const [editingCell, setEditingCell] = useState(null); // { itemId, field }
  const [editValue, setEditValue] = useState("");
  
  // Item selection for email
  const [selectedItems, setSelectedItems] = useState({});
  
  // Common items modal state
  const [commonItemsModal, setCommonItemsModal] = useState({
    isOpen: false,
    selected: {}
  });
  
  // Currency management
  const defaultCurrency = tender?.currency || "USD";
  // Use custom rates if available, otherwise fall back to static rates
  const customRates = tender?.customExchangeRates || {};
  const exchangeRates = { ...STATIC_EXCHANGE_RATES, ...customRates };
  
  // Exchange rate manager modal state
  const [exchangeRateModal, setExchangeRateModal] = useState({
    isOpen: false,
    rates: {}
  });
  
  const boqFileInputRef = useRef(null);
  
  // Resizable columns state
  const [columnWidths, setColumnWidths] = useState({
    checkbox: 40,
    srno: 45,
    item: 150,
    description: 180,
    subsystem: 120,
    qty: 60,
    unit: 60,
    make: 100,
    model: 100,
    remarks: 120,
    hsnCode: 100,
    countryOfOrigin: 120,
    deliveryTerms: 120,
    currency: 65,
    price: 100,
    duty: 120,  // Increased width to show duty amount + percentage
    total: 150,  // Further expanded width for better total price visibility
    landedCost: 150,  // Total landed cost including all Incoterm-based costs
    actions: 50
  });
  const [resizing, setResizing] = useState(null); // { column, startX, startWidth }
  const tableRef = useRef(null);
  
  // CSV upload ref
  const csvFileInputRef = useRef(null);
  
  // Tab navigation for costing sheet
  const [activeSubTab, setActiveSubTab] = useState("costing"); // "costing", "analysis", "calculations", "scenarios"
  
  // Integrated Freight Calculator state
  // Freight calculator modal state - simplified from inline expandable to modal popup
  // Removed: cartonGroups, activeGroupId (unused grouping features)
  const [freightCalculatorModal, setFreightCalculatorModal] = useState({
    isOpen: false,
    selectedItems: {},
    packaging: {
      itemsPerCarton: "",
      length: "",
      width: "",
      height: "",
      weight: "",
      freightMode: "Sea"
    },
    calculating: false,
    results: null
  });
  
  // AI Analysis state
  const [analysisResults, setAnalysisResults] = useState({
    categoryBreakdown: [],
    abnormalPrices: [],
    hiddenCharges: [],
    mistakes: [],
    suggestions: []
  });
  
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // Margin Calculations state
  const [calculationRates, setCalculationRates] = useState(DEFAULT_CALCULATION_RATES);
  const [calculatedCosts, setCalculatedCosts] = useState({
    baseCost: 0,
    taxes: 0,
    freight: 0,
    bufferStock: 0,
    contingency: 0,
    grossMargin: 0,
    netMargin: 0,
    opex: 0,
    insurance: 0,
    duty: 0,
    totalCost: 0,
    sellingPrice: 0
  });
  
  // Scenario Planning state
  const [scenarios, setScenarios] = useState([]);
  // currentScenario is used for detailed scenario view (future enhancement)
  // eslint-disable-next-line no-unused-vars
  const [currentScenario, setCurrentScenario] = useState(null);
  const [scenarioModal, setScenarioModal] = useState({
    isOpen: false,
    name: "",
    description: "",
    modifications: []
  });
  
  // Cost Breakdown Modal (shows Incoterm responsibilities)
  const [costBreakdownModal, setcostBreakdownModal] = useState({
    isOpen: false,
    item: null
  });

  // Handle column resize start
  const handleResizeStart = (e, column) => {
    e.preventDefault();
    setResizing({
      column,
      startX: e.clientX,
      startWidth: columnWidths[column]
    });
  };

  // Handle column resize move
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!resizing) return;
      
      const diff = e.clientX - resizing.startX;
      const newWidth = Math.max(40, resizing.startWidth + diff); // Minimum 40px
      
      setColumnWidths(prev => ({
        ...prev,
        [resizing.column]: newWidth
      }));
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    if (resizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing]);

  // Fetch costing items from Firestore
  const fetchCostingItems = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, "tenders", tenderId, "costingItems"),
        orderBy("createdAt", "asc")
      );
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setCostingItems(items);
    } catch (error) {
      console.error("Error fetching costing items:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch subsystems from Firestore
  const fetchSubsystems = async () => {
    try {
      const q = query(
        collection(db, "tenders", tenderId, "subsystems"),
        orderBy("name", "asc")
      );
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setSubsystems(items);
    } catch (error) {
      console.error("Error fetching subsystems:", error);
      // Subsystems are optional, so don't show error to user
    }
  };

  useEffect(() => {
    if (tenderId) {
      fetchCostingItems();
      fetchSubsystems();
      // Set first document as selected if available
      if (tender?.files && tender.files.length > 0) {
        setSelectedDocUrl(tender.files[0].url);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenderId, tender]);

  // Visual drag rectangle rendering (inject into DOM)
  useEffect(() => {
    const pageElement = document.querySelector('.react-pdf__Page');
    if (!pageElement) return;

    // Clear existing highlights
    pageElement.querySelectorAll('.costing-drag-highlight').forEach(el => el.remove());

    // Add the active drag rectangle if dragging
    if (isDragging && dragRect) {
      const highlightDiv = document.createElement('div');
      highlightDiv.className = 'costing-drag-highlight';
      
      // Apply coordinates
      highlightDiv.style.position = 'absolute';
      highlightDiv.style.top = `${dragRect.top}px`;
      highlightDiv.style.left = `${dragRect.left}px`;
      highlightDiv.style.width = `${dragRect.width}px`;
      highlightDiv.style.height = `${dragRect.height}px`;
      highlightDiv.style.pointerEvents = 'none';
      highlightDiv.style.borderRadius = '3px';
      highlightDiv.style.backgroundColor = 'rgba(59, 130, 246, 0.25)'; // Blue tint
      highlightDiv.style.border = '2px dashed rgba(59, 130, 246, 0.6)';
      highlightDiv.style.zIndex = '9';
      
      pageElement.appendChild(highlightDiv);
    }

    // Cleanup
    return () => {
      pageElement.querySelectorAll('.costing-drag-highlight').forEach(el => el.remove());
    };
  }, [isDragging, dragRect]);

  // PDF handlers
  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setPageNumber(1);
  }

  function onDocumentLoadError(error) {
    console.error("Error loading PDF:", error);
  }

  const handlePageChange = (newPageNumber) => {
    setPageNumber(newPageNumber);
    setDragRect(null);
    setSelectedText("");
  };

  // Drag-and-highlight handlers (same pattern as compliance tab)
  const handleMouseDown = (e) => {
    // Only start dragging if clicking on the PDF page itself
    const pageElement = document.querySelector('.react-pdf__Page');
    if (!pageElement) return;
    
    // Cache the page element reference for performance
    pageElementRef.current = pageElement;
    
    const pageRect = pageElement.getBoundingClientRect();
    
    // Check if the click is within the PDF page
    if (e.clientX < pageRect.left || e.clientX > pageRect.right ||
        e.clientY < pageRect.top || e.clientY > pageRect.bottom) {
      return;
    }
    
    const startX = e.clientX - pageRect.left;
    const startY = e.clientY - pageRect.top;
    
    // Store the start position
    setDragStart({ x: startX, y: startY });
    setDragRect(null);
  };
  
  const handleMouseMove = (e) => {
    if (!dragStart) return;
    
    // Use cached reference to avoid repeated DOM queries
    const pageElement = pageElementRef.current;
    if (!pageElement) {
      setIsDragging(false);
      setDragStart(null);
      return;
    }
    
    const pageRect = pageElement.getBoundingClientRect();
    const currentX = e.clientX - pageRect.left;
    const currentY = e.clientY - pageRect.top;
    
    // Calculate movement distance
    const deltaX = Math.abs(currentX - dragStart.x);
    const deltaY = Math.abs(currentY - dragStart.y);
    
    // Only start dragging if mouse has moved at least 5 pixels
    if (!isDragging && (deltaX > 5 || deltaY > 5)) {
      setIsDragging(true);
      // Clear any text selection when starting to drag
      window.getSelection().removeAllRanges();
    }
    
    if (isDragging) {
      // Calculate the drag rectangle
      const left = Math.min(dragStart.x, currentX);
      const top = Math.min(dragStart.y, currentY);
      const width = Math.abs(currentX - dragStart.x);
      const height = Math.abs(currentY - dragStart.y);
      
      setDragRect({ top, left, width, height });
    }
  };
  
  const handleMouseUp = async () => {
    // Handle drag completion
    if (isDragging && dragRect && dragRect.width > 10 && dragRect.height > 10) {
      // Extract text from the highlighted area
      const pageElement = pageElementRef.current;
      const textLayer = document.querySelector('.react-pdf__Page__textContent');
      
      let extractedText = '';
      
      if (pageElement && textLayer) {
        const textSpans = Array.from(textLayer.querySelectorAll('span'));
        const pageRect = pageElement.getBoundingClientRect();
        
        textSpans.forEach(span => {
          const spanRect = span.getBoundingClientRect();
          const spanRelativeTop = spanRect.top - pageRect.top;
          const spanRelativeLeft = spanRect.left - pageRect.left;
          const spanRelativeBottom = spanRect.bottom - pageRect.top;
          const spanRelativeRight = spanRect.right - pageRect.left;
          
          // Check if span intersects with the highlight rectangle
          const intersects = !(
            spanRelativeRight < dragRect.left ||
            spanRelativeLeft > dragRect.left + dragRect.width ||
            spanRelativeBottom < dragRect.top ||
            spanRelativeTop > dragRect.top + dragRect.height
          );
          
          if (intersects) {
            extractedText += (span.textContent || '') + ' ';
          }
        });
      }
      
      // Automatically add to costing sheet if text was extracted
      if (extractedText.trim()) {
        try {
          await addDoc(collection(db, "tenders", tenderId, "costingItems"), {
            itemName: extractedText.trim().substring(0, 100), // Use first 100 chars as item name
            description: extractedText.trim().length > 100 ? extractedText.trim().substring(100, 300) : "",
            quantity: 0,
            unit: "Unit",
            make: "",
            model: "",
            remarks: "",
            hsnCode: "",
            countryOfOrigin: "",
            deliveryTerms: "",
            currency: defaultCurrency,
            unitPrice: 0,
            dutyAmount: 0,
            dutyRate: 0,
            totalPrice: 0,
            category: "Material",
            page: pageNumber,
            createdAt: new Date(),
          });
          
          fetchCostingItems();
          setSelectedText("");
        } catch (error) {
          console.error("Error adding item from drag:", error);
          alert("Failed to add item");
        }
      }
    } else if (!isDragging) {
      // Fallback to old text selection behavior for compatibility
      const selection = window.getSelection();
      const text = selection.toString().trim();
      if (text) {
        setSelectedText(text);
      }
    }
    
    // Reset drag state
    setIsDragging(false);
    setDragStart(null);
    setDragRect(null);
    pageElementRef.current = null;
  };
  
  const handleMouseLeave = () => {
    // When mouse leaves, cancel the drag without creating an item
    if (isDragging) {
      setIsDragging(false);
      setDragStart(null);
      setDragRect(null);
      pageElementRef.current = null;
    }
  };

  // AI extraction of BOQ items line by line
  const handleAiExtractBOQ = async () => {
    if (!selectedDocUrl) {
      alert("Please select a document first");
      return;
    }

    setAiLoading(true);
    try {
      // Extract actual text from the PDF page
      const pageText = await extractPageText(selectedDocUrl, pageNumber);
      
      if (!pageText || pageText.length < MIN_TEXT_LENGTH_FOR_AI) {
        alert("Page seems empty or text extraction failed. Try a different page or add items manually.");
        setAiLoading(false);
        return;
      }

      const response = await fetch("/api/extract-document", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          type: "boq",
          documentUrl: selectedDocUrl,
          pageNumber: pageNumber
        }),
      });

      if (!response.ok) throw new Error("AI Extraction Failed");

      const data = await response.json();
      const items = Array.isArray(data.items) ? data.items : [];

      if (items.length > 0) {
        // Save all BOQ items to Firestore (without rates - user will add manually)
        const promises = items.map((item) =>
          addDoc(collection(db, "tenders", tenderId, "costingItems"), {
            itemName: item.itemName || "BOQ Item",
            description: item.description || "",
            quantity: parseFloat(item.quantity) || 0,
            unit: item.unit || "Unit",
            make: item.make || "",
            model: item.model || "",
            remarks: item.remarks || "",
            hsnCode: item.hsnCode || "",
            countryOfOrigin: item.countryOfOrigin || "",
            deliveryTerms: item.deliveryTerms || "",
            freightMode: item.freightMode || "Sea",
            currency: defaultCurrency,
            unitPrice: 0, // User will add rate manually
            dutyAmount: 0,
            shippingAmount: 0,
            totalPrice: 0,
            category: item.category || "Material",
            page: pageNumber,
            createdAt: new Date(),
          })
        );
        await Promise.all(promises);
        fetchCostingItems();
        alert(`Success! Extracted ${items.length} BOQ items from page ${pageNumber}. Add rates manually.`);
      } else {
        alert("No BOQ items found on this page. Try a different page or add items manually.");
      }
    } catch (error) {
      console.error("AI BOQ Extraction Error:", error);
      alert("AI Extraction Failed. Please add BOQ items manually.");
    } finally {
      setAiLoading(false);
    }
  };

  // Upload BOQ document
  const handleUploadBOQ = async (files) => {
    if (!files || files.length === 0) return;
    
    try {
      const file = files[0];
      const fileRef = ref(storage, `tenders/${tenderId}/boq/${file.name}`);
      await uploadBytes(fileRef, file);
      await getDownloadURL(fileRef);
      
      alert("BOQ document uploaded successfully! Select it from the document dropdown to extract items.");
      
      // Optionally add to tender files
      // You could update the tender document here to include this BOQ file
    } catch (error) {
      console.error("Error uploading BOQ:", error);
      alert("Failed to upload BOQ document");
    } finally {
      if (boqFileInputRef.current) boqFileInputRef.current.value = null;
    }
  };

  // Inline cell editing handlers
  const handleCellClick = (itemId, field, currentValue) => {
    setEditingCell({ itemId, field });
    setEditValue(currentValue !== undefined && currentValue !== null ? String(currentValue) : "");
  };

  const handleCellChange = (e) => {
    setEditValue(e.target.value);
  };

  const handleCellBlur = async () => {
    if (!editingCell) return;

    const { itemId, field } = editingCell;
    const item = costingItems.find(i => i.id === itemId);
    
    if (!item) {
      setEditingCell(null);
      return;
    }

    try {
      let updateData = {};
      
      if (field === "quantity" || field === "unitPrice") {
        const numValue = parseFloat(editValue) || 0;
        updateData[field] = numValue;
        
        // Recalculate total price including existing duty (but NOT shipping - handled separately)
        const qty = field === "quantity" ? numValue : (item.quantity || 0);
        const price = field === "unitPrice" ? numValue : (item.unitPrice || 0);
        const basePrice = qty * price;
        // Include existing duty amount only (shipping is separate now)
        updateData.totalPrice = basePrice + (item.dutyAmount || 0);
      } else {
        updateData[field] = editValue;
      }

      const itemRef = doc(db, "tenders", tenderId, "costingItems", itemId);
      await updateDoc(itemRef, updateData);
      
      // Update local state
      const updatedItem = { ...item, ...updateData };
      setCostingItems(prev => prev.map(i => 
        i.id === itemId ? updatedItem : i
      ));

      // Auto-suggest HSN code if item name is updated and HSN is empty
      if (field === "itemName" && canSuggestHSN(updatedItem) && !updatedItem.hsnCode) {
        // Auto-suggest HSN code with delay to avoid rapid API calls
        setTimeout(() => {
          handleSuggestHSNCode(updatedItem);
        }, HSN_SUGGESTION_DELAY_MS);
      }

      // Auto-calculate duty and shipping if relevant fields were updated
      if (field === "hsnCode" || field === "countryOfOrigin" || field === "deliveryTerms" || 
          field === "quantity" || field === "unitPrice") {
        // Trigger async calculation which will update duty/shipping and recalculate total
        calculateItemDutyAndShipping(updatedItem);
        
        // Apply Incoterm rules when delivery terms change
        if (field === "deliveryTerms") {
          applyIncotermRules(updatedItem);
        }
      }
    } catch (error) {
      console.error("Error updating cell:", error);
    } finally {
      setEditingCell(null);
      setEditValue("");
    }
  };

  const handleCellKeyDown = (e) => {
    if (e.key === "Enter") {
      e.target.blur();
    } else if (e.key === "Escape") {
      setEditingCell(null);
      setEditValue("");
    }
  };

  // Delete costing item (no confirmation)
  const handleDeleteItem = async (itemId, itemName) => {
    try {
      await deleteDoc(doc(db, "tenders", tenderId, "costingItems", itemId));
      setCostingItems(prev => prev.filter(i => i.id !== itemId));
    } catch (error) {
      console.error("Error deleting item:", error);
      alert("Failed to delete item");
    }
  };

  // Add new empty row
  const handleAddRow = async () => {
    try {
      const docRef = await addDoc(collection(db, "tenders", tenderId, "costingItems"), {
        itemName: "New Item",
        description: "",
        quantity: 0,
        unit: "Unit",
        make: "",
        model: "",
        remarks: "",
        hsnCode: "",
        countryOfOrigin: "",
        deliveryTerms: "",
        currency: defaultCurrency,
        unitPrice: 0,
        dutyAmount: 0,
        dutyRate: 0,
        totalPrice: 0,
        category: "Material",
        subsystemId: "", // Add subsystem field
        // New Incoterm-based cost fields
        freightCost: 0,
        insuranceCost: 0,
        portCharges: 0,
        customsDutyTax: 0,
        localDelivery: 0,
        totalLandedCost: 0,
        createdAt: new Date(),
      });
      
      fetchCostingItems();
      
      // Auto-focus on the new item's name cell
      setTimeout(() => {
        handleCellClick(docRef.id, "itemName", "New Item");
      }, 100);
    } catch (error) {
      console.error("Error adding row:", error);
      alert("Failed to add row");
    }
  };

  // AI Accessory Suggestion - opens modal for selection
  // Helper function to check if HSN code can be suggested
  const canSuggestHSN = (item) => {
    return item && item.itemName && item.itemName.trim().length >= MIN_ITEM_NAME_LENGTH;
  };

  // Suggest HSN code based on item name
  const handleSuggestHSNCode = async (item) => {
    if (!canSuggestHSN(item)) return;
    
    // Skip if already suggesting, already has HSN code, or user is editing HSN field
    if (suggestingHSN[item.id] || item.hsnCode || 
        (editingCell?.itemId === item.id && editingCell?.field === "hsnCode")) return;
    
    setSuggestingHSN(prev => ({ ...prev, [item.id]: true }));
    try {
      const response = await fetch("/api/suggest-hsn-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          itemName: item.itemName,
          description: item.description
        }),
      });

      if (!response.ok) throw new Error("HSN Suggestion Failed");

      const data = await response.json();
      
      if (data.success && data.hsnCode) {
        // Update the item with suggested HSN code
        const itemRef = doc(db, "tenders", tenderId, "costingItems", item.id);
        await updateDoc(itemRef, {
          hsnCode: data.hsnCode
        });
        
        // Update local state
        const updatedItem = { ...item, hsnCode: data.hsnCode };
        setCostingItems(prev => prev.map(i => 
          i.id === item.id ? updatedItem : i
        ));

        // Trigger auto-calculation of duty and shipping with small delay
        setTimeout(() => {
          calculateItemDutyAndShipping(updatedItem);
        }, CALCULATION_TRIGGER_DELAY_MS);
      }
    } catch (error) {
      console.error("HSN Code Suggestion Error:", error);
      // Silently fail for auto-suggestions - don't show alert
    } finally {
      setSuggestingHSN(prev => ({ ...prev, [item.id]: false }));
    }
  };

  const handleSuggestAccessories = async (itemName) => {
    if (!itemName || itemName.trim().length < MIN_ITEM_NAME_LENGTH) return;
    
    setSuggestingAccessories(true);
    try {
      const response = await fetch("/api/suggest-accessories", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          itemName,
          currency: tender?.currency || "USD"
        }),
      });

      if (!response.ok) throw new Error("AI Suggestion Failed");

      const data = await response.json();
      let accessories = Array.isArray(data.accessories) ? data.accessories : [];

      // Filter out accessories that already exist in BOQ
      // Check by item name (case-insensitive)
      const existingItemNames = costingItems.map(item => 
        item.itemName?.toLowerCase().trim()
      ).filter(Boolean);
      
      accessories = accessories.filter(acc => {
        const accName = acc.itemName?.toLowerCase().trim();
        return accName && !existingItemNames.includes(accName);
      });

      if (accessories.length > 0) {
        // Open modal with accessories for selection
        const initialSelected = {};
        accessories.forEach((_, idx) => {
          initialSelected[idx] = true; // Select all by default
        });
        
        setAccessoryModal({
          isOpen: true,
          itemName,
          accessories,
          selected: initialSelected
        });
      } else {
        alert(`No new accessories to suggest for "${itemName}" (existing items filtered out)`);
      }
    } catch (error) {
      console.error("AI Accessory Suggestion Error:", error);
      alert("Failed to get AI suggestions. Please try again.");
    } finally {
      setSuggestingAccessories(false);
    }
  };

  // Toggle accessory selection in modal
  const handleToggleAccessory = (index) => {
    setAccessoryModal(prev => ({
      ...prev,
      selected: {
        ...prev.selected,
        [index]: !prev.selected[index]
      }
    }));
  };

  // Select/deselect all accessories
  const handleSelectAllAccessories = (selectAll) => {
    const newSelected = {};
    accessoryModal.accessories.forEach((_, idx) => {
      newSelected[idx] = selectAll;
    });
    setAccessoryModal(prev => ({
      ...prev,
      selected: newSelected
    }));
  };

  // Add selected accessories to costing sheet
  const handleAddSelectedAccessories = async () => {
    const selectedAccessories = accessoryModal.accessories.filter((_, idx) => accessoryModal.selected[idx]);
    
    if (selectedAccessories.length === 0) {
      alert("Please select at least one accessory to add.");
      return;
    }

    try {
      const promises = selectedAccessories.map((acc) =>
        addDoc(collection(db, "tenders", tenderId, "costingItems"), {
          itemName: acc.itemName || "Accessory",
          description: acc.description || "",
          quantity: parseFloat(acc.quantity) || 1,
          unit: acc.unit || "nos",
          make: "",
          model: "",
          remarks: `AI suggested for: ${accessoryModal.itemName}`,
          hsnCode: "",
          countryOfOrigin: "",
          deliveryTerms: "",
          currency: defaultCurrency,
          unitPrice: 0,
          dutyAmount: 0,
          dutyRate: 0,
          totalPrice: 0,
          category: "Material",
          createdAt: new Date(),
        })
      );
      await Promise.all(promises);
      fetchCostingItems();
      
      // Close modal and show success
      setAccessoryModal({ isOpen: false, itemName: "", accessories: [], selected: {} });
      alert(`Added ${selectedAccessories.length} selected accessories successfully!`);
    } catch (error) {
      console.error("Error adding accessories:", error);
      alert("Failed to add accessories. Please try again.");
    }
  };

  // Close accessory modal
  const handleCloseAccessoryModal = () => {
    setAccessoryModal({ isOpen: false, itemName: "", accessories: [], selected: {} });
  };

  // Email selected items to vendor
  const handleEmailSelectedItems = () => {
    const selected = costingItems.filter(item => selectedItems[item.id]);
    
    if (selected.length === 0) {
      alert("Please select at least one item to email.");
      return;
    }

    // Calculate column widths for proper alignment
    const maxItemLen = Math.max(15, ...selected.map(i => (i.itemName || "").length));
    const maxDescLen = Math.min(40, Math.max(15, ...selected.map(i => (i.description || "").length)));
    const maxMakeLen = Math.max(10, ...selected.map(i => (i.make || "").length));
    const maxModelLen = Math.max(10, ...selected.map(i => (i.model || "").length));

    // Create properly formatted text table for email
    const divider = "+" + "-".repeat(4) + "+" + "-".repeat(maxItemLen + 2) + "+" + "-".repeat(maxDescLen + 2) + "+" + "-".repeat(8) + "+" + "-".repeat(8) + "+" + "-".repeat(maxMakeLen + 2) + "+" + "-".repeat(maxModelLen + 2) + "+";
    
    const header = `| ${"#".padEnd(2)} | ${"Item Name".padEnd(maxItemLen)} | ${"Description".padEnd(maxDescLen)} | ${"Qty".padEnd(6)} | ${"Unit".padEnd(6)} | ${"Make".padEnd(maxMakeLen)} | ${"Model".padEnd(maxModelLen)} |`;
    
    const rows = selected.map((item, idx) => {
      const num = String(idx + 1).padEnd(2);
      const name = (item.itemName || "").substring(0, maxItemLen).padEnd(maxItemLen);
      const desc = (item.description || "").substring(0, maxDescLen).padEnd(maxDescLen);
      const qty = String(item.quantity || "").padEnd(6);
      const unit = (item.unit || "").padEnd(6);
      const make = (item.make || "").substring(0, maxMakeLen).padEnd(maxMakeLen);
      const model = (item.model || "").substring(0, maxModelLen).padEnd(maxModelLen);
      return `| ${num} | ${name} | ${desc} | ${qty} | ${unit} | ${make} | ${model} |`;
    }).join('\n');

    const formattedTable = `${divider}\n${header}\n${divider}\n${rows}\n${divider}`;

    // Create email body (plain text format for mailto compatibility)
    const emailBody = `Dear Vendor,

We are requesting quotations for the following ${selected.length} item(s) from ${tender?.title || "Project"}:

═══════════════════════════════════════════════════════════════════════════════
                              ITEMS FOR QUOTATION
═══════════════════════════════════════════════════════════════════════════════

${formattedTable}

───────────────────────────────────────────────────────────────────────────────
                                DETAILED LIST
───────────────────────────────────────────────────────────────────────────────
${selected.map((item, idx) => 
`[${idx + 1}] ${item.itemName || "Item"}
    ├─ Description : ${item.description || "N/A"}
    ├─ Quantity    : ${item.quantity || "TBD"} ${item.unit || ""}
    ├─ Make/Model  : ${item.make || "Any"} / ${item.model || "Any"}
    └─ Remarks     : ${item.remarks || "None"}`
).join('\n\n')}

───────────────────────────────────────────────────────────────────────────────
                            QUOTATION REQUIREMENTS
───────────────────────────────────────────────────────────────────────────────
Please provide your best quotation including:

  ✓ Unit Price (${tender?.currency || "USD"})
  ✓ Total Price (${tender?.currency || "USD"})
  ✓ Delivery Time
  ✓ Warranty Terms
  ✓ Payment Terms

═══════════════════════════════════════════════════════════════════════════════

Best Regards`;

    const subject = encodeURIComponent(`Quote Request: ${selected.length} Items - ${tender?.title || "Project"}`);
    const body = encodeURIComponent(emailBody);

    // Create mailto link
    const mailtoUrl = `mailto:?subject=${subject}&body=${body}`;
    
    // Use anchor element click to avoid page navigation
    const link = document.createElement("a");
    link.href = mailtoUrl;
    link.click();
  };

  // Open common items modal
  const handleOpenCommonItems = () => {
    const initialSelected = {};
    COMMON_ITEMS.forEach((_, idx) => {
      initialSelected[idx] = false; // None selected by default
    });
    setCommonItemsModal({
      isOpen: true,
      selected: initialSelected
    });
  };

  // Toggle common item selection
  const handleToggleCommonItem = (index) => {
    setCommonItemsModal(prev => ({
      ...prev,
      selected: {
        ...prev.selected,
        [index]: !prev.selected[index]
      }
    }));
  };

  // Select/deselect all common items
  const handleSelectAllCommonItems = (selectAll) => {
    const newSelected = {};
    COMMON_ITEMS.forEach((_, idx) => {
      newSelected[idx] = selectAll;
    });
    setCommonItemsModal(prev => ({
      ...prev,
      selected: newSelected
    }));
  };

  // Add selected common items to BOQ
  const handleAddCommonItems = async () => {
    const selectedCommonItems = COMMON_ITEMS.filter((_, idx) => commonItemsModal.selected[idx]);
    
    if (selectedCommonItems.length === 0) {
      alert("Please select at least one item to add.");
      return;
    }

    try {
      const promises = selectedCommonItems.map((itemName) =>
        addDoc(collection(db, "tenders", tenderId, "costingItems"), {
          itemName: itemName,
          description: "",
          quantity: 1,
          unit: "LS", // Lump Sum
          make: "",
          model: "",
          remarks: "Common project item",
          hsnCode: "",
          countryOfOrigin: "",
          deliveryTerms: "",
          currency: defaultCurrency,
          unitPrice: 0,
          dutyAmount: 0,
          dutyRate: 0,
          totalPrice: 0,
          category: "General",
          createdAt: new Date(),
        })
      );
      await Promise.all(promises);
      fetchCostingItems();
      
      // Close modal and show success
      setCommonItemsModal({ isOpen: false, selected: {} });
      alert(`Added ${selectedCommonItems.length} common items successfully!`);
    } catch (error) {
      console.error("Error adding common items:", error);
      alert("Failed to add common items. Please try again.");
    }
  };

  // Close common items modal
  const handleCloseCommonItems = () => {
    setCommonItemsModal({ isOpen: false, selected: {} });
  };

  // Open exchange rate manager
  const handleOpenExchangeRateManager = () => {
    // Initialize with current rates (custom or static)
    const initialRates = {};
    SUPPORTED_CURRENCIES.forEach(currency => {
      initialRates[currency.code] = exchangeRates[currency.code];
    });
    setExchangeRateModal({ isOpen: true, rates: initialRates });
  };

  // Update exchange rate in modal
  const handleUpdateExchangeRate = (currencyCode, value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue <= 0) return;
    
    setExchangeRateModal(prev => ({
      ...prev,
      rates: {
        ...prev.rates,
        [currencyCode]: numValue
      }
    }));
  };

  // Save custom exchange rates to tender document
  const handleSaveExchangeRates = async () => {
    try {
      const tenderRef = doc(db, "tenders", tenderId);
      await updateDoc(tenderRef, {
        customExchangeRates: exchangeRateModal.rates,
        exchangeRatesUpdatedAt: new Date()
      });
      
      // Refresh tender data to get new rates
      window.location.reload(); // Simple reload to refresh tender data
      
      setExchangeRateModal({ isOpen: false, rates: {} });
      alert("Exchange rates updated successfully!");
    } catch (error) {
      console.error("Error saving exchange rates:", error);
      alert("Failed to save exchange rates. Please try again.");
    }
  };

  // Reset to default static rates
  const handleResetExchangeRates = async () => {
    if (!window.confirm("Reset all exchange rates to default values? This will remove your custom rates.")) {
      return;
    }
    
    try {
      const tenderRef = doc(db, "tenders", tenderId);
      await updateDoc(tenderRef, {
        customExchangeRates: {},
        exchangeRatesUpdatedAt: new Date()
      });
      
      // Refresh tender data
      window.location.reload();
      
      setExchangeRateModal({ isOpen: false, rates: {} });
      alert("Exchange rates reset to default values!");
    } catch (error) {
      console.error("Error resetting exchange rates:", error);
      alert("Failed to reset exchange rates. Please try again.");
    }
  };

  // Close exchange rate manager
  const handleCloseExchangeRateManager = () => {
    setExchangeRateModal({ isOpen: false, rates: {} });
  };

  // Currency conversion helper
  // exchangeRates format: 1 unit of currency = X USD
  const convertCurrency = (amount, fromCurrency, toCurrency) => {
    if (fromCurrency === toCurrency) return amount;
    
    // Convert from source currency to USD
    const amountInUSD = amount * exchangeRates[fromCurrency];
    
    // Convert from USD to target currency
    const convertedAmount = amountInUSD / exchangeRates[toCurrency];
    
    return convertedAmount;
  };

  // Get currency symbol
  const getCurrencySymbol = (currencyCode) => {
    const currency = SUPPORTED_CURRENCIES.find(c => c.code === currencyCode);
    return currency?.symbol || currencyCode;
  };

  // Helper function for currency formatting
  const formatCurrency = (amount, currency = "USD") => {
    const symbol = getCurrencySymbol(currency);
    return `${symbol} ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format with conversion display
  const formatCurrencyWithConversion = (amount, itemCurrency, showConversion = true) => {
    const formattedAmount = formatCurrency(amount, itemCurrency);
    
    if (!showConversion || itemCurrency === defaultCurrency) {
      return formattedAmount;
    }
    
    const convertedAmount = convertCurrency(amount, itemCurrency, defaultCurrency);
    const formattedConverted = formatCurrency(convertedAmount, defaultCurrency);
    
    const rate = (exchangeRates[itemCurrency] / exchangeRates[defaultCurrency]).toFixed(4);
    return (
      <span title={`Exchange rate: 1 ${itemCurrency} = ${rate} ${defaultCurrency}`}>
        {formattedConverted} <span style={{ color: '#64748b', fontSize: '11px' }}>({formattedAmount})</span>
      </span>
    );
  };

  // Generate Scenario Snapshot PDF
  const generateScenarioPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    let yPos = 20;

    // Title
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('Scenario Planning Snapshot', pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    // Project Info
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Project: ${tender?.title || 'Untitled Project'}`, 14, yPos);
    yPos += 6;
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 14, yPos);
    yPos += 6;
    doc.text(`Currency: ${defaultCurrency}`, 14, yPos);
    yPos += 12;

    // Current Scenario
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Current Scenario', 14, yPos);
    yPos += 8;

    doc.autoTable({
      startY: yPos,
      head: [['Factor', 'Value']],
      body: [
        ['Base Cost', formatCurrency(calculatedCosts.baseCost, defaultCurrency)],
        ['Lead Time Risk', `${calculationRates.leadTimeRisk}% - ${formatCurrency(calculatedCosts.leadTimeRisk, defaultCurrency)}`],
        ['Currency Volatility', `${calculationRates.currencyVolatility}% - ${formatCurrency(calculatedCosts.currencyVolatility, defaultCurrency)}`],
        ['Warranty Handling', `${calculationRates.warrantyHandling}% - ${formatCurrency(calculatedCosts.warrantyHandling, defaultCurrency)}`],
        ['Contingency', `${calculationRates.contingency}% - ${formatCurrency(calculatedCosts.contingency, defaultCurrency)}`],
        ['Risk Buffer', `${calculationRates.risk}% - ${formatCurrency(calculatedCosts.risk, defaultCurrency)}`],
        ['Business Agent', `${calculationRates.businessAgent}% - ${formatCurrency(calculatedCosts.businessAgent, defaultCurrency)}`],
        ['COGS', `${calculationRates.cogs}% - ${formatCurrency(calculatedCosts.cogs, defaultCurrency)}`],
        ['Total Cost (before margin)', formatCurrency(calculatedCosts.totalCost, defaultCurrency)],
        ['Margin', `${calculationRates.margin}% - ${formatCurrency(calculatedCosts.margin, defaultCurrency)}`],
      ],
      theme: 'grid',
      headStyles: { fillColor: [71, 85, 105], fontSize: 10 },
      bodyStyles: { fontSize: 9 },
      columnStyles: { 0: { cellWidth: 60 }, 1: { cellWidth: 'auto', halign: 'right' } }
    });
    yPos = doc.lastAutoTable.finalY + 12;

    // Scenarios Comparison
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Scenario Comparison', 14, yPos);
    yPos += 8;

    const scenarios = [
      { 
        name: 'Worst Case', 
        factors: { leadTimeRisk: 10, currencyVolatility: 10, warrantyHandling: 5, contingency: 15, risk: 12, businessAgent: calculationRates.businessAgent, cogs: calculationRates.cogs, margin: calculationRates.margin }
      },
      { 
        name: 'Current', 
        factors: calculationRates 
      },
      { 
        name: 'Best Case', 
        factors: { leadTimeRisk: 2, currencyVolatility: 2, warrantyHandling: 1, contingency: 3, risk: 2, businessAgent: calculationRates.businessAgent, cogs: calculationRates.cogs, margin: calculationRates.margin }
      },
      { 
        name: 'Internal Approval', 
        factors: { leadTimeRisk: 5, currencyVolatility: 5, warrantyHandling: 3, contingency: 5, risk: 5, businessAgent: calculationRates.businessAgent, cogs: calculationRates.cogs, margin: 22 }
      }
    ];

    const scenarioData = scenarios.map(s => {
      const base = calculatedCosts.baseCost;
      const costs = base * (1 + (s.factors.leadTimeRisk + s.factors.currencyVolatility + s.factors.warrantyHandling + s.factors.contingency + s.factors.risk + (s.factors.businessAgent || 0) + (s.factors.cogs || 0)) / 100);
      const margin = costs * (s.factors.margin / 100);
      const total = costs + margin;
      return [
        s.name,
        formatCurrency(total, defaultCurrency),
        `${s.factors.margin}%`,
        formatCurrency(margin, defaultCurrency)
      ];
    });

    doc.autoTable({
      startY: yPos,
      head: [['Scenario', 'Total Cost to Bid', 'Margin %', 'Profit Amount']],
      body: scenarioData,
      theme: 'striped',
      headStyles: { fillColor: [71, 85, 105], fontSize: 10 },
      bodyStyles: { fontSize: 9 }
    });
    yPos = doc.lastAutoTable.finalY + 12;

    // Vendor Discount Table
    if (yPos > pageHeight - 60) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Vendor Discount Impact', 14, yPos);
    yPos += 8;

    const discountData = [5, 10, 15, 20].map(discount => {
      const discountedBase = calculatedCosts.baseCost * (1 - discount / 100);
      const discountedCosts = discountedBase * (1 + (calculationRates.leadTimeRisk + calculationRates.currencyVolatility + calculationRates.warrantyHandling + calculationRates.contingency + calculationRates.risk + calculationRates.businessAgent + calculationRates.cogs) / 100);
      const discountedMargin = discountedCosts * (calculationRates.margin / 100);
      const discountedTotal = discountedCosts + discountedMargin;
      const savings = calculatedCosts.totalCostToBid - discountedTotal;
      const marginIncrease = discountedMargin - calculatedCosts.margin;
      
      return [
        `${discount}%`,
        formatCurrency(discountedTotal, defaultCurrency),
        formatCurrency(savings, defaultCurrency),
        formatCurrency(marginIncrease, defaultCurrency)
      ];
    });

    doc.autoTable({
      startY: yPos,
      head: [['Vendor Discount', 'New Total', 'Savings', 'Margin Boost']],
      body: discountData,
      theme: 'grid',
      headStyles: { fillColor: [5, 150, 105], fontSize: 10 },
      bodyStyles: { fontSize: 9 }
    });
    yPos = doc.lastAutoTable.finalY + 12;

    // Recommendations
    if (yPos > pageHeight - 40) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Pricing Recommendations', 14, yPos);
    yPos += 8;

    const recommendedSellingPrice = calculatedCosts.totalCostToBid;
    const minimumSellingPrice = calculatedCosts.totalCost * 1.05;
    const breakEvenPrice = calculatedCosts.totalCost;

    doc.autoTable({
      startY: yPos,
      head: [['Recommendation', 'Price', 'Notes']],
      body: [
        ['Recommended', formatCurrency(recommendedSellingPrice, defaultCurrency), `With ${calculationRates.margin}% margin`],
        ['Minimum', formatCurrency(minimumSellingPrice, defaultCurrency), 'Floor price (5% margin)'],
        ['Break-even', formatCurrency(breakEvenPrice, defaultCurrency), 'Never go below this!']
      ],
      theme: 'striped',
      headStyles: { fillColor: [102, 126, 234], fontSize: 10 },
      bodyStyles: { fontSize: 9 }
    });

    // Summary Note
    yPos = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(10);
    doc.setFont(undefined, 'italic');
    const summaryText = `Quote at ${formatCurrency(calculatedCosts.totalCostToBid, defaultCurrency)} with ${calculationRates.margin}% margin. Can negotiate down to ${formatCurrency(calculatedCosts.totalCost * 1.05, defaultCurrency)} (5% margin) if needed.`;
    const splitText = doc.splitTextToSize(summaryText, pageWidth - 28);
    doc.text(splitText, 14, yPos);

    // Save PDF
    const fileName = `Scenario_Snapshot_${tender?.title?.replace(/[^a-z0-9]/gi, '_') || 'Project'}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  // Calculate line total in default currency (with conversions)
  const lineTotal = costingItems.reduce((sum, item) => {
    const itemCurrency = item.currency || defaultCurrency;
    const totalPrice = parseFloat(item.totalPrice) || 0;
    const convertedPrice = convertCurrency(totalPrice, itemCurrency, defaultCurrency);
    return sum + convertedPrice;
  }, 0);

  // Render editable cell
  const renderEditableCell = (item, field, width = "100%", type = "text") => {
    const isEditing = editingCell?.itemId === item.id && editingCell?.field === field;
    const value = item[field];

    if (isEditing) {
      return (
        <input
          type={type}
          value={editValue}
          onChange={handleCellChange}
          onBlur={handleCellBlur}
          onKeyDown={handleCellKeyDown}
          autoFocus
          style={{
            width,
            padding: "4px 8px",
            border: "2px solid #2563eb",
            borderRadius: "4px",
            fontSize: "14px",
          }}
          step={type === "number" ? "0.01" : undefined}
        />
      );
    }

    return (
      <div
        onClick={() => handleCellClick(item.id, field, value)}
        style={{
          cursor: "pointer",
          padding: "4px 8px",
          minHeight: "24px",
          borderRadius: "4px",
          transition: "background 0.2s",
        }}
        className="editable-cell"
      >
        {field === "unitPrice" || field === "totalPrice" 
          ? formatCurrency(value, item.currency || defaultCurrency)
          : (value !== undefined && value !== null ? value : "—")}
      </div>
    );
  };

  // ============ CSV EXPORT/IMPORT FUNCTIONS ============
  
  // Helper function to escape CSV values
  const escapeCsvValue = (value) => {
    if (value === null || value === undefined) return '""';
    const stringValue = String(value);
    // Escape double quotes by doubling them, wrap in quotes
    return `"${stringValue.replace(/"/g, '""')}"`;
  };

  // Export costing sheet to CSV
  const handleExportCSV = () => {
    if (costingItems.length === 0) {
      alert("No items to export. Add items first.");
      return;
    }

    const headers = [
      "Sr No", "Item Name", "Description", "Quantity", "Unit", 
      "Make", "Model", "Remarks", "HSN Code", "Country of Origin", 
      "Delivery Terms", "Currency", "Unit Price", "Duty Amount", "Duty Rate %", "Total Price", "Category"
    ];

    const rows = costingItems.map((item, idx) => [
      idx + 1,
      escapeCsvValue(item.itemName),
      escapeCsvValue(item.description),
      item.quantity || 0,
      item.unit || "Unit",
      escapeCsvValue(item.make),
      escapeCsvValue(item.model),
      escapeCsvValue(item.remarks),
      escapeCsvValue(item.hsnCode),
      escapeCsvValue(item.countryOfOrigin),
      escapeCsvValue(item.deliveryTerms),
      item.currency || defaultCurrency,
      item.unitPrice || 0,
      item.dutyAmount || 0,
      item.dutyRate || 0,
      item.totalPrice || 0,
      item.category || "Material"
    ]);

    const csvContent = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    
    link.setAttribute("href", url);
    link.setAttribute("download", `CostingSheet_${tender?.clientName || tenderId}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Import costing sheet from CSV
  const handleImportCSV = async (files) => {
    if (!files || files.length === 0) return;
    
    const file = files[0];
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          alert("CSV file is empty or has no data rows.");
          return;
        }

        // Parse header row to get column indices
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
        
        const getColumnIndex = (names) => {
          for (const name of names) {
            const idx = headers.findIndex(h => h.includes(name.toLowerCase()));
            if (idx !== -1) return idx;
          }
          return -1;
        };

        const colIndices = {
          itemName: getColumnIndex(["item name", "item", "name", "description"]),
          description: getColumnIndex(["description", "desc"]),
          quantity: getColumnIndex(["quantity", "qty", "amount"]),
          unit: getColumnIndex(["unit", "uom"]),
          make: getColumnIndex(["make", "brand", "manufacturer"]),
          model: getColumnIndex(["model", "part"]),
          remarks: getColumnIndex(["remarks", "notes", "comment"]),
          hsnCode: getColumnIndex(["hsn code", "hsn", "hs code"]),
          countryOfOrigin: getColumnIndex(["country of origin", "origin", "origin country"]),
          deliveryTerms: getColumnIndex(["delivery terms", "delivery", "incoterms", "terms"]),
          currency: getColumnIndex(["currency", "curr"]),
          unitPrice: getColumnIndex(["unit price", "price", "rate", "cost"]),
          dutyAmount: getColumnIndex(["duty amount", "duty", "tax"]),
          dutyRate: getColumnIndex(["duty rate", "duty %", "duty percent"]),
          totalPrice: getColumnIndex(["total price", "total", "amount"]),
          category: getColumnIndex(["category", "type"])
        };

        const parsedItems = [];
        
        for (let i = 1; i < lines.length; i++) {
          const values = parseCSVLine(lines[i]);
          if (values.length === 0) continue;

          const getValue = (idx) => idx >= 0 && idx < values.length ? values[idx]?.replace(/^"|"$/g, '').trim() : "";
          
          const itemName = getValue(colIndices.itemName);
          if (!itemName) continue; // Skip rows without item name

          const quantity = parseFloat(getValue(colIndices.quantity)) || 0;
          const unitPrice = parseFloat(getValue(colIndices.unitPrice)) || 0;
          const dutyAmount = parseFloat(getValue(colIndices.dutyAmount)) || 0;
          const dutyRate = parseFloat(getValue(colIndices.dutyRate)) || 0;
          
          parsedItems.push({
            itemName,
            description: getValue(colIndices.description),
            quantity,
            unit: getValue(colIndices.unit) || "Unit",
            make: getValue(colIndices.make),
            model: getValue(colIndices.model),
            remarks: getValue(colIndices.remarks),
            hsnCode: getValue(colIndices.hsnCode),
            countryOfOrigin: getValue(colIndices.countryOfOrigin),
            deliveryTerms: getValue(colIndices.deliveryTerms),
            currency: getValue(colIndices.currency) || defaultCurrency,
            unitPrice,
            dutyAmount,
            dutyRate,
            totalPrice: (quantity * unitPrice) + dutyAmount,
            category: getValue(colIndices.category) || "Material",
            createdAt: new Date()
          });
        }

        if (parsedItems.length === 0) {
          alert("No valid items found in CSV. Make sure the file has headers and data.");
          return;
        }

        // Confirm before import
        if (!window.confirm(`Found ${parsedItems.length} items. Import them to the costing sheet?`)) {
          return;
        }

        // Add items to Firestore
        const promises = parsedItems.map(item => 
          addDoc(collection(db, "tenders", tenderId, "costingItems"), item)
        );
        await Promise.all(promises);
        
        fetchCostingItems();
        alert(`Successfully imported ${parsedItems.length} items!`);
      } catch (error) {
        console.error("Error importing CSV:", error);
        alert("Error parsing CSV file. Please check the format.");
      }
    };

    reader.readAsText(file);
    if (csvFileInputRef.current) csvFileInputRef.current.value = null;
  };

  // Helper function to parse CSV line (handles quoted values)
  const parseCSVLine = (line) => {
    const values = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current);
    
    return values;
  };

  // ============ AI ANALYSIS FUNCTIONS ============
  
  // Run AI analysis on costing items
  const handleRunAnalysis = async () => {
    if (costingItems.length === 0) {
      alert("No items to analyze. Add items first.");
      return;
    }

    setIsAnalyzing(true);
    
    try {
      // Category breakdown
      const categoryBreakdown = COST_CATEGORIES.map(cat => {
        const items = costingItems.filter(item => 
          (item.category || "material").toLowerCase() === cat.id.toLowerCase() ||
          (item.itemName || "").toLowerCase().includes(cat.name.toLowerCase())
        );
        const total = items.reduce((sum, item) => sum + (parseFloat(item.totalPrice) || 0), 0);
        return { ...cat, count: items.length, total, items };
      }).filter(cat => cat.count > 0);

      // Detect abnormal prices (items with prices significantly different from average)
      const itemsWithPrices = costingItems.filter(item => parseFloat(item.unitPrice) > 0);
      const averagePrice = itemsWithPrices.length > 0 
        ? itemsWithPrices.reduce((sum, item) => sum + parseFloat(item.unitPrice), 0) / itemsWithPrices.length 
        : 0;
      
      const abnormalPrices = costingItems.filter(item => {
        const price = parseFloat(item.unitPrice) || 0;
        if (price === 0) return false;
        // Flag if price is more than 5x average or less than 1/5 of average
        return price > averagePrice * 5 || (averagePrice > 0 && price < averagePrice / 5);
      }).map(item => ({
        ...item,
        reason: parseFloat(item.unitPrice) > averagePrice * 5 
          ? `Price is ${(parseFloat(item.unitPrice) / averagePrice).toFixed(1)}x the average (${formatCurrency(averagePrice, defaultCurrency)})`
          : `Price is only ${((parseFloat(item.unitPrice) / averagePrice) * 100).toFixed(1)}% of average`
      }));

      // Detect hidden charges (items that might be extras/additionals)
      const hiddenChargeKeywords = ["additional", "extra", "hidden", "surcharge", "processing", "handling", "admin", "management"];
      const hiddenCharges = costingItems.filter(item => {
        const name = (item.itemName || "").toLowerCase();
        const desc = (item.description || "").toLowerCase();
        return hiddenChargeKeywords.some(kw => name.includes(kw) || desc.includes(kw));
      });

      // Detect common mistakes
      const mistakes = [];
      
      // Check for zero prices
      const zeroPriceItems = costingItems.filter(item => !parseFloat(item.unitPrice));
      if (zeroPriceItems.length > 0) {
        mistakes.push({
          type: "zero_price",
          severity: "high",
          message: `${zeroPriceItems.length} item(s) have zero or missing prices`,
          items: zeroPriceItems
        });
      }

      // Check for duplicate items
      const itemNames = costingItems.map(item => (item.itemName || "").toLowerCase().trim());
      const duplicates = itemNames.filter((name, idx) => 
        name && itemNames.indexOf(name) !== idx
      );
      if (duplicates.length > 0) {
        mistakes.push({
          type: "double_count",
          severity: "medium",
          message: `${[...new Set(duplicates)].length} item(s) may be duplicated`,
          items: costingItems.filter(item => duplicates.includes((item.itemName || "").toLowerCase().trim()))
        });
      }

      // Check for copy-paste errors (identical quantities and prices)
      const priceQtyPairs = costingItems.map(item => `${item.quantity}-${item.unitPrice}`);
      const suspiciousPairs = priceQtyPairs.filter((pair, idx) => 
        pair !== "0-0" && priceQtyPairs.indexOf(pair) !== idx
      );
      if (suspiciousPairs.length > 0) {
        mistakes.push({
          type: "copy_paste",
          severity: "low",
          message: `${suspiciousPairs.length} item(s) have identical qty/price combinations (possible copy-paste)`,
          items: costingItems.filter((item, idx) => suspiciousPairs.includes(priceQtyPairs[idx]))
        });
      }

      // Generate suggestions
      const suggestions = [];
      
      if (categoryBreakdown.length < 3) {
        suggestions.push({
          type: "categorization",
          message: "Consider categorizing items for better cost visibility",
          action: "Review and assign categories to items"
        });
      }

      if (!costingItems.some(item => (item.itemName || "").toLowerCase().includes("contingency"))) {
        suggestions.push({
          type: "contingency",
          message: "No contingency item found",
          action: "Add a contingency buffer (typically 5-10% of total cost)"
        });
      }

      if (!costingItems.some(item => (item.itemName || "").toLowerCase().includes("freight") || 
                                      (item.itemName || "").toLowerCase().includes("shipping"))) {
        suggestions.push({
          type: "freight",
          message: "No freight/shipping cost included",
          action: "Consider adding freight costs if applicable"
        });
      }

      setAnalysisResults({
        categoryBreakdown,
        abnormalPrices,
        hiddenCharges,
        mistakes,
        suggestions
      });

    } catch (error) {
      console.error("Analysis error:", error);
      alert("Error running analysis. Please try again.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  // ============ MARGIN CALCULATION FUNCTIONS ============
  
  // Auto-calculate duty for a single item
  const calculateItemDutyAndShipping = async (item) => {
    // Check if we have minimum required fields for duty calculation
    const canCalculateDuty = item.hsnCode && item.countryOfOrigin && tender?.projectCountry;
    
    if (!canCalculateDuty) {
      return; // Nothing to calculate
    }

    try {
      let dutyAmount = 0;
      let dutyRate = 0;

      // Calculate Duty
      try {
        const dutyResponse = await fetch("/api/calculate-duty", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            hsnCode: item.hsnCode,
            countryOfOrigin: item.countryOfOrigin,
            projectCountry: tender.projectCountry,
            itemName: item.itemName,
            itemDescription: item.description,
            unitPrice: item.unitPrice || 0,
            currency: item.currency || defaultCurrency
          })
        });
        
        const dutyData = await dutyResponse.json();
        
        if (dutyData.success !== false && dutyData.dutyRate) {
          dutyRate = dutyData.dutyRate;
          // Calculate duty amount: (unitPrice * quantity * dutyRate / 100)
          const baseAmount = (item.unitPrice || 0) * (item.quantity || 0);
          dutyAmount = baseAmount * dutyRate / 100;
        }
      } catch (error) {
        console.error("Error calculating duty:", error);
      }

      // Update the item with calculated amounts and recalculate total
      const basePrice = (item.unitPrice || 0) * (item.quantity || 0);
      const newTotalPrice = basePrice + dutyAmount;

      const itemRef = doc(db, "tenders", tenderId, "costingItems", item.id);
      await updateDoc(itemRef, {
        dutyAmount: Math.round(dutyAmount * 100) / 100, // Round to 2 decimals
        dutyRate: Math.round(dutyRate * 100) / 100, // Store duty rate percentage
        totalPrice: Math.round(newTotalPrice * 100) / 100
      });
      
      // Update local state
      setCostingItems(prev => prev.map(i => 
        i.id === item.id ? { 
          ...i, 
          dutyAmount: Math.round(dutyAmount * 100) / 100,
          dutyRate: Math.round(dutyRate * 100) / 100,
          totalPrice: Math.round(newTotalPrice * 100) / 100
        } : i
      ));

    } catch (error) {
      console.error("Error calculating duty for item:", item.itemName, error);
    }
  };

  // AI-based duty calculation
  const [calculatingDuty, setCalculatingDuty] = useState(false);
  const handleCalculateAIDuty = async () => {
    setCalculatingDuty(true);
    try {
      // Get items with HSN code and origin
      const itemsWithHSN = costingItems.filter(item => 
        item.hsnCode && item.countryOfOrigin && tender?.projectCountry
      );
      
      if (itemsWithHSN.length === 0) {
        alert("No items found with HSN Code and Country of Origin. Please ensure Project Country is set in RFP details and add HSN Code and Country of Origin to items.");
        setCalculatingDuty(false);
        return;
      }
      
      // Calculate average duty rate from all items
      let totalDutyRate = 0;
      let successCount = 0;
      
      for (const item of itemsWithHSN.slice(0, MAX_ITEMS_FOR_AI_CALCULATION)) { // Limit items to avoid excessive API calls
        try {
          const response = await fetch("/api/calculate-duty", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              hsnCode: item.hsnCode,
              countryOfOrigin: item.countryOfOrigin,
              projectCountry: tender.projectCountry,
              itemName: item.itemName,
              itemDescription: item.description,
              unitPrice: item.unitPrice,
              currency: item.currency || defaultCurrency
            })
          });
          
          const data = await response.json();
          if (data.success !== false && data.dutyRate) {
            totalDutyRate += data.dutyRate;
            successCount++;
          }
        } catch (error) {
          console.error("Error calculating duty for item:", item.itemName, error);
        }
      }
      
      if (successCount > 0) {
        const averageDutyRate = totalDutyRate / successCount;
        updateCalculationRate('dutyRate', averageDutyRate);
        const limitNote = itemsWithHSN.length > MAX_ITEMS_FOR_AI_CALCULATION 
          ? ` (analyzed ${MAX_ITEMS_FOR_AI_CALCULATION} of ${itemsWithHSN.length} items to optimize API costs)` 
          : '';
        alert(`AI calculated average duty rate: ${averageDutyRate.toFixed(2)}% based on ${successCount} item(s)${limitNote}`);
      } else {
        alert("Failed to calculate duty rate. Please check your HSN codes and countries.");
      }
    } catch (error) {
      console.error("Error in AI duty calculation:", error);
      alert("Error calculating duty. Please try again.");
    } finally {
      setCalculatingDuty(false);
    }
  };
  
  // AI-based shipping calculation
  const [calculatingShipping, setCalculatingShipping] = useState(false);
  const handleCalculateAIShipping = async () => {
    setCalculatingShipping(true);
    try {
      // Get items with delivery terms and countries
      const itemsWithDelivery = costingItems.filter(item => 
        item.deliveryTerms && item.countryOfOrigin && tender?.projectCountry
      );
      
      if (itemsWithDelivery.length === 0) {
        alert("No items found with Delivery Terms and Country of Origin. Please ensure Project Country is set in RFP details and add Delivery Terms and Country of Origin to items.");
        setCalculatingShipping(false);
        return;
      }
      
      // Calculate average shipping rate from all items
      let totalShippingRate = 0;
      let successCount = 0;
      
      for (const item of itemsWithDelivery.slice(0, MAX_ITEMS_FOR_AI_CALCULATION)) { // Limit items to avoid excessive API calls
        try {
          const response = await fetch("/api/calculate-shipping", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              deliveryTerms: item.deliveryTerms,
              countryOfOrigin: item.countryOfOrigin,
              projectCountry: tender.projectCountry,
              itemName: item.itemName,
              itemDescription: item.description,
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              totalValue: item.totalPrice,
              currency: item.currency || defaultCurrency,
              // Master carton mode fields
              make: item.make,
              model: item.model,
              freightMode: item.freightMode
            })
          });
          
          const data = await response.json();
          if (data.mode === "master_carton" && data.shippingCostUSD) {
            // For master carton mode, convert to percentage for the average
            const baseAmount = (item.unitPrice || 0) * (item.quantity || 0);
            if (baseAmount > 0) {
              const percentageRate = (data.shippingCostUSD / baseAmount) * 100;
              totalShippingRate += percentageRate;
              successCount++;
            }
          } else if (data.success === true && data.shippingRate) {
            totalShippingRate += data.shippingRate;
            successCount++;
          }
        } catch (error) {
          console.error("Error calculating shipping for item:", item.itemName, error);
        }
      }
      
      if (successCount > 0) {
        const averageShippingRate = totalShippingRate / successCount;
        updateCalculationRate('freightRate', averageShippingRate);
        const limitNote = itemsWithDelivery.length > MAX_ITEMS_FOR_AI_CALCULATION 
          ? ` (analyzed ${MAX_ITEMS_FOR_AI_CALCULATION} of ${itemsWithDelivery.length} items to optimize API costs)` 
          : '';
        alert(`AI calculated average shipping rate: ${averageShippingRate.toFixed(2)}% based on ${successCount} item(s)${limitNote}`);
      } else {
        alert("Failed to calculate shipping rate. Please check your delivery terms and countries.");
      }
    } catch (error) {
      console.error("Error in AI shipping calculation:", error);
      alert("Error calculating shipping. Please try again.");
    } finally {
      setCalculatingShipping(false);
    }
  };

  // AI-based currency volatility calculation
  const [calculatingVolatility, setCalculatingVolatility] = useState(false);
  const handleCalculateCurrencyVolatility = async () => {
    setCalculatingVolatility(true);
    try {
      const fromCurrency = defaultCurrency;
      const toCurrency = tender?.projectCountry === 'India' ? 'INR' : 'USD'; // Example logic
      
      const response = await fetch("/api/suggest-margin-factors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: 'volatility',
          fromCurrency,
          toCurrency,
          totalCost: calculatedCosts.baseCost
        })
      });
      
      const data = await response.json();
      if (data.success && data.suggestedBuffer) {
        updateCalculationRate('currencyVolatility', data.suggestedBuffer);
        alert(`AI Suggested Currency Volatility Buffer: ${data.suggestedBuffer}%\n\nReasoning: ${data.reasoning}`);
      } else {
        alert("Failed to calculate currency volatility. Please try again.");
      }
    } catch (error) {
      console.error("Error calculating currency volatility:", error);
      alert("Error calculating currency volatility. Please try again.");
    } finally {
      setCalculatingVolatility(false);
    }
  };

  // AI-based contingency calculation
  const [calculatingContingency, setCalculatingContingency] = useState(false);
  const handleCalculateContingency = async () => {
    setCalculatingContingency(true);
    try {
      const response = await fetch("/api/suggest-margin-factors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: 'contingency',
          costingItems,
          totalCost: calculatedCosts.baseCost,
          projectType: tender?.projectType,
          deliveryTimeline: tender?.submissionDate
        })
      });
      
      const data = await response.json();
      if (data.success && data.suggestedContingency) {
        updateCalculationRate('contingency', data.suggestedContingency);
        alert(`AI Suggested Contingency: ${data.suggestedContingency}%\n\nReasoning: ${data.reasoning}`);
      } else {
        alert("Failed to calculate contingency. Please try again.");
      }
    } catch (error) {
      console.error("Error calculating contingency:", error);
      alert("Error calculating contingency. Please try again.");
    } finally {
      setCalculatingContingency(false);
    }
  };

  // AI-based risk calculation
  const [calculatingRisk, setCalculatingRisk] = useState(false);
  const handleCalculateRisk = async () => {
    setCalculatingRisk(true);
    try {
      const response = await fetch("/api/suggest-margin-factors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: 'risk',
          costingItems,
          totalCost: calculatedCosts.baseCost,
          projectCountry: tender?.projectCountry,
          deliveryTerms: costingItems[0]?.deliveryTerms,
          supplierReliability: 'medium' // Could be enhanced with actual data
        })
      });
      
      const data = await response.json();
      if (data.success && data.suggestedRisk) {
        updateCalculationRate('risk', data.suggestedRisk);
        alert(`AI Suggested Risk Buffer: ${data.suggestedRisk}%\n\nReasoning: ${data.reasoning}`);
      } else {
        alert("Failed to calculate risk. Please try again.");
      }
    } catch (error) {
      console.error("Error calculating risk:", error);
      alert("Error calculating risk. Please try again.");
    } finally {
      setCalculatingRisk(false);
    }
  };
  
  // Calculate all margins and costs
  const calculateMargins = () => {
    // Base cost is the sum of all material/equipment items
    const materialItems = costingItems.filter(item => 
      !["tax", "freight", "insurance", "finance", "contingency", "margin", "overhead", "discount"]
        .includes((item.category || "").toLowerCase())
    );
    
    const baseCost = materialItems.reduce((sum, item) => {
      const itemCurrency = item.currency || defaultCurrency;
      const totalPrice = parseFloat(item.totalPrice) || 0;
      return sum + convertCurrency(totalPrice, itemCurrency, defaultCurrency);
    }, 0);

    // Calculate individual components based on new structure
    const leadTimeRisk = baseCost * (calculationRates.leadTimeRisk / 100);
    const currencyVolatility = baseCost * (calculationRates.currencyVolatility / 100);
    const warrantyHandling = baseCost * (calculationRates.warrantyHandling / 100);
    const contingency = baseCost * (calculationRates.contingency / 100);
    const risk = baseCost * (calculationRates.risk / 100);
    const businessAgent = baseCost * (calculationRates.businessAgent / 100);
    const cogs = baseCost * (calculationRates.cogs / 100);
    
    // Total cost before margin (sum of all risk factors)
    const totalCostBeforeMargin = baseCost + leadTimeRisk + currencyVolatility + warrantyHandling + contingency + risk + businessAgent + cogs;
    
    // Margin calculation
    const margin = totalCostBeforeMargin * (calculationRates.margin / 100);
    
    // Total cost to bid (includes margin)
    const totalCostToBid = totalCostBeforeMargin + margin;

    setCalculatedCosts({
      baseCost,
      leadTimeRisk,
      currencyVolatility,
      warrantyHandling,
      contingency,
      risk,
      businessAgent,
      cogs,
      margin,
      totalCost: totalCostBeforeMargin,
      totalCostToBid
    });
  };

  // Update calculation rate
  const updateCalculationRate = (field, value) => {
    const numValue = parseFloat(value) || 0;
    setCalculationRates(prev => ({ ...prev, [field]: numValue }));
  };

  // Recalculate when items or rates change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (costingItems.length > 0) {
      calculateMargins();
    }
  }, [costingItems, calculationRates]);

  // ============ SCENARIO PLANNING FUNCTIONS ============
  
  // Create new scenario
  const createScenario = (name, description) => {
    const newScenario = {
      id: Date.now(),
      name,
      description,
      createdAt: new Date().toISOString(),
      modifications: [],
      baseTotal: lineTotal,
      projectedTotal: lineTotal
    };
    setScenarios(prev => [...prev, newScenario]);
    setCurrentScenario(newScenario);
    return newScenario;
  };

  // Delete scenario
  const deleteScenario = (scenarioId) => {
    if (window.confirm("Are you sure you want to delete this scenario?")) {
      setScenarios(prev => prev.filter(s => s.id !== scenarioId));
      if (currentScenario?.id === scenarioId) {
        setCurrentScenario(null);
      }
    }
  };

  // Edit scenario
  const updateScenario = (scenarioId, updates) => {
    setScenarios(prev => prev.map(s => {
      if (s.id === scenarioId) {
        return { ...s, ...updates };
      }
      return s;
    }));
  };

  // Add modification to scenario
  const addScenarioModification = (scenarioId, modification) => {
    setScenarios(prev => prev.map(s => {
      if (s.id === scenarioId) {
        const newMods = [...s.modifications, modification];
        const projectedTotal = calculateScenarioTotal(s.baseTotal, newMods);
        return { ...s, modifications: newMods, projectedTotal };
      }
      return s;
    }));
  };

  // Calculate scenario total with modifications
  const calculateScenarioTotal = (baseTotal, modifications) => {
    let total = baseTotal;
    modifications.forEach(mod => {
      switch (mod.type) {
        case "percentage_change":
          total = total * (1 + mod.value / 100);
          break;
        case "fixed_add":
          total = total + mod.value;
          break;
        case "fixed_subtract":
          total = total - mod.value;
          break;
        case "replace_item":
          total = total - (mod.originalCost || 0) + (mod.newCost || 0);
          break;
        default:
          break;
      }
    });
    return total;
  };

  // Handle scenario modal
  const handleOpenScenarioModal = () => {
    setScenarioModal({
      isOpen: true,
      name: `Scenario ${scenarios.length + 1}`,
      description: "",
      modifications: []
    });
  };

  const handleCloseScenarioModal = () => {
    setScenarioModal({ isOpen: false, name: "", description: "", modifications: [] });
  };

  const handleSaveScenario = () => {
    if (!scenarioModal.name.trim()) {
      alert("Please enter a scenario name");
      return;
    }
    createScenario(scenarioModal.name, scenarioModal.description);
    handleCloseScenarioModal();
  };

  // ========== INTEGRATED FREIGHT CALCULATOR HANDLERS ==========
  
  // Constants for freight calculations
  const FREIGHT_INCLUSIVE_INCOTERMS = ["CFR", "CIF", "CPT", "CIP", "DAP", "DPU", "DDP"];
  const AIR_VOLUMETRIC_DIVISOR = 5000; // Standard air freight volumetric weight formula: (L×W×H)/5000
  const FREIGHT_HSN_CODE = "9965"; // HSN code for freight/transportation services
  const DEFAULT_AIR_RATE_PER_KG = 12; // USD per kg (should be looked up from freight-rates.json based on route)
  const DEFAULT_SEA_RATE_PER_CBM = 95; // USD per CBM (should be looked up from freight-rates.json based on route)
  
  // Helper function to validate numeric input
  // By default requires value > minValue (inclusive=false), or value >= minValue (inclusive=true)
  const validateNumericInput = (value, fieldName, minValue = 0, inclusive = false) => {
    const parsed = parseFloat(value);
    const isValid = inclusive 
      ? !isNaN(parsed) && parsed >= minValue
      : !isNaN(parsed) && parsed > minValue;
    
    if (!isValid) {
      const comparison = inclusive ? 'at least' : 'greater than';
      return { valid: false, error: `${fieldName} must be a valid number ${comparison} ${minValue}.` };
    }
    return { valid: true, value: parsed };
  };
  
  // Get eligible items for freight calculation (items where BUYER is responsible for freight)
  const getEligibleFreightItems = () => {
    return costingItems.filter(item => {
      // Only show items where buyer pays freight based on Incoterm responsibilities
      const responsibilities = getIncotermResponsibilities(item.deliveryTerms);
      return (
        item.make && 
        item.make.trim() !== "" && 
        item.model && 
        item.model.trim() !== "" &&
        item.deliveryTerms &&
        responsibilities &&
        responsibilities.freight === "you"  // Only items where buyer pays freight
      );
    });
  };
  
  const handleOpenFreightCalculator = () => {
    setFreightCalculatorModal(prev => ({
      ...prev,
      isOpen: true
    }));
  };
  
  const handleCloseFreightCalculator = () => {
    setFreightCalculatorModal({
      isOpen: false,
      selectedItems: {},
      packaging: {
        itemsPerCarton: "",
        length: "",
        width: "",
        height: "",
        weight: "",
        freightMode: "Sea"
      },
      calculating: false,
      results: null
    });
  };
  
  const handleToggleFreightItem = (itemId) => {
    setFreightCalculatorModal(prev => ({
      ...prev,
      selectedItems: {
        ...prev.selectedItems,
        [itemId]: !prev.selectedItems[itemId]
      }
    }));
  };
  
  const handleFreightPackagingChange = (field, value) => {
    setFreightCalculatorModal(prev => ({
      ...prev,
      packaging: {
        ...prev.packaging,
        [field]: value
      }
    }));
  };
  
  const handleCalculateFreight = async () => {
    const selectedItemIds = Object.keys(freightCalculatorModal.selectedItems).filter(
      id => freightCalculatorModal.selectedItems[id]
    );
    
    if (selectedItemIds.length === 0) {
      alert("Please select at least one item to calculate freight.");
      return;
    }
    
    const packaging = freightCalculatorModal.packaging;
    
    setFreightCalculatorModal(prev => ({ ...prev, calculating: true }));
    
    try {
      const selectedItems = costingItems.filter(item => selectedItemIds.includes(item.id));
      const totalQty = selectedItems.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
      const avgCurrency = selectedItems[0]?.currency || defaultCurrency;
      
      // Validate total quantity
      const qtyValidation = validateNumericInput(totalQty, "Total quantity", 0);
      if (!qtyValidation.valid) {
        alert("Selected items must have valid quantities greater than 0.");
        setFreightCalculatorModal(prev => ({ ...prev, calculating: false }));
        return;
      }
      
      // Validate and parse items per carton
      const itemsPerCartonValidation = validateNumericInput(packaging.itemsPerCarton, "Items per carton", 0);
      if (!itemsPerCartonValidation.valid) {
        alert(itemsPerCartonValidation.error);
        setFreightCalculatorModal(prev => ({ ...prev, calculating: false }));
        return;
      }
      const numCartons = Math.ceil(totalQty / itemsPerCartonValidation.value);
      
      // Validate and parse dimensions
      const lengthValidation = validateNumericInput(packaging.length, "Length", 0);
      const widthValidation = validateNumericInput(packaging.width, "Width", 0);
      const heightValidation = validateNumericInput(packaging.height, "Height", 0);
      const weightValidation = validateNumericInput(packaging.weight, "Weight", 0);
      
      if (!lengthValidation.valid || !widthValidation.valid || 
          !heightValidation.valid || !weightValidation.valid) {
        const invalidField = !lengthValidation.valid ? lengthValidation.error :
                            !widthValidation.valid ? widthValidation.error :
                            !heightValidation.valid ? heightValidation.error :
                            weightValidation.error;
        alert(invalidField);
        setFreightCalculatorModal(prev => ({ ...prev, calculating: false }));
        return;
      }
      
      const length = lengthValidation.value;
      const width = widthValidation.value;
      const height = heightValidation.value;
      const weight = weightValidation.value;
      
      let freightCostUSD = 0;
      let calculation = "";
      
      if (packaging.freightMode === "Air") {
        // Air freight: volumetric weight calculation
        const volumetricWeight = (length * width * height) / AIR_VOLUMETRIC_DIVISOR;
        const chargeableWeight = Math.max(volumetricWeight, weight);
        
        // Use default rate (in production, this should lookup from freight-rates.json based on origin-destination)
        const ratePerKg = DEFAULT_AIR_RATE_PER_KG;
        freightCostUSD = numCartons * chargeableWeight * ratePerKg;
        calculation = `${numCartons} cartons × ${chargeableWeight.toFixed(2)} kg × $${ratePerKg}/kg = $${freightCostUSD.toFixed(2)}`;
      } else {
        // Sea freight: CBM calculation
        const cbm = (length / 100) * (width / 100) * (height / 100);
        const ratePerCBM = DEFAULT_SEA_RATE_PER_CBM;
        freightCostUSD = numCartons * cbm * ratePerCBM;
        calculation = `${numCartons} cartons × ${cbm.toFixed(3)} CBM × $${ratePerCBM}/CBM = $${freightCostUSD.toFixed(2)}`;
      }
      
      // Convert to item currency with safe division
      const exchangeRate = exchangeRates[avgCurrency];
      if (!exchangeRate || exchangeRate === 0) {
        throw new Error(`Invalid exchange rate for currency: ${avgCurrency}`);
      }
      const usdToTargetRate = 1 / exchangeRate;
      const freightCostItemCurrency = freightCostUSD * usdToTargetRate;
      
      setFreightCalculatorModal(prev => ({
        ...prev,
        results: {
          numCartons,
          freightCostUSD,
          freightCostItemCurrency,
          currency: avgCurrency,
          calculation,
          selectedItems: selectedItems.map(item => ({ id: item.id, name: item.item }))
        },
        calculating: false
      }));
    } catch (error) {
      console.error("Error calculating freight:", error);
      alert("Failed to calculate freight. Please try again.");
      setFreightCalculatorModal(prev => ({ ...prev, calculating: false }));
    }
  };
  
  const handleAddFreightToCosting = async () => {
    if (!freightCalculatorModal.results) {
      alert("Please calculate freight first.");
      return;
    }
    
    try {
      const results = freightCalculatorModal.results;
      const itemNames = results.selectedItems.map(item => item.name).join(", ");
      const freightItemName = `Freight - ${itemNames.substring(0, 50)}${itemNames.length > 50 ? "..." : ""}`;
      
      // Create freight line item
      const freightItem = {
        itemName: freightItemName,
        description: `Freight cost for ${results.selectedItems.length} item(s). ${results.calculation}`,
        subsystem: "Logistics",
        quantity: 1,
        unit: "Lot",
        make: "N/A",
        model: "N/A",
        remarks: `${freightCalculatorModal.packaging.freightMode} freight, ${results.numCartons} carton(s)`,
        hsnCode: FREIGHT_HSN_CODE, // HSN code for freight/transportation services
        countryOfOrigin: tender?.projectCountry || "",
        deliveryTerms: "",
        currency: results.currency,
        unitPrice: results.freightCostItemCurrency,
        dutyAmount: 0,
        dutyRate: 0,
        totalPrice: results.freightCostItemCurrency,
        freightCost: 0,
        insuranceCost: 0,
        portCharges: 0,
        customsDutyTax: 0,
        localDelivery: 0,
        totalLandedCost: results.freightCostItemCurrency,
        category: "Freight",
        createdAt: new Date()
      };
      
      await addDoc(collection(db, "tenders", tenderId, "costingItems"), freightItem);
      
      alert(`Freight cost added to costing sheet: ${formatCurrency(results.freightCostItemCurrency, results.currency)}`);
      
      // Close modal and reset
      handleCloseFreightCalculator();
      
      fetchCostingItems(); // Refresh the list
    } catch (error) {
      console.error("Error adding freight to costing:", error);
      alert("Failed to add freight to costing sheet. Please try again.");
    }
  };

  // ========== INCOTERM-BASED COST CALCULATION FUNCTIONS ==========

  /**
   * Apply Incoterm rules to an item and calculate all cost components
   * This is the main function that orchestrates cost calculation based on Incoterm
   */
  const applyIncotermRules = async (item) => {
    if (!item || !item.deliveryTerms) return;
    
    const responsibilities = getIncotermResponsibilities(item.deliveryTerms);
    if (!responsibilities) return;
    
    const itemRef = doc(db, "tenders", tenderId, "costingItems", item.id);
    const updateData = {};
    
    const basePrice = parseFloat(item.unitPrice) || 0;
    const quantity = parseFloat(item.quantity) || 0;
    const totalBasePrice = basePrice * quantity;
    
    // Apply cost responsibilities based on Incoterm
    // If vendor covers (responsibility = "vendor"), set cost to 0
    // If buyer covers (responsibility = "you"), calculate or allow input
    
    if (responsibilities.freight === "vendor") {
      updateData.freightCost = 0;
    }
    
    if (responsibilities.insurance === "vendor") {
      updateData.insuranceCost = 0;
    } else if (responsibilities.insurance === "you") {
      // Calculate insurance as 0.5% of total value (standard rate)
      updateData.insuranceCost = totalBasePrice * 0.005;
    }
    
    if (responsibilities.portCharges === "vendor") {
      updateData.portCharges = 0;
    } else if (responsibilities.portCharges === "you") {
      // Port charges - could be a fixed amount or percentage
      // For now, use a small percentage (0.3%)
      updateData.portCharges = totalBasePrice * 0.003;
    }
    
    if (responsibilities.customsDuty === "vendor") {
      updateData.customsDutyTax = 0;
    } else if (responsibilities.customsDuty === "you") {
      // Calculate customs duty based on HSN code and country
      const dutyAmount = await calculateCustomsDuty(item);
      updateData.customsDutyTax = dutyAmount;
    }
    
    if (responsibilities.localDelivery === "vendor") {
      updateData.localDelivery = 0;
    } else if (responsibilities.localDelivery === "you") {
      // Local delivery - estimate as 1% of total value
      updateData.localDelivery = totalBasePrice * 0.01;
    }
    
    // Calculate total landed cost
    const freightCost = updateData.freightCost !== undefined ? updateData.freightCost : (item.freightCost || 0);
    const insuranceCost = updateData.insuranceCost !== undefined ? updateData.insuranceCost : (item.insuranceCost || 0);
    const portCharges = updateData.portCharges !== undefined ? updateData.portCharges : (item.portCharges || 0);
    const customsDutyTax = updateData.customsDutyTax !== undefined ? updateData.customsDutyTax : (item.customsDutyTax || 0);
    const localDelivery = updateData.localDelivery !== undefined ? updateData.localDelivery : (item.localDelivery || 0);
    
    updateData.totalLandedCost = totalBasePrice + freightCost + insuranceCost + portCharges + customsDutyTax + localDelivery;
    
    // Update in Firestore
    try {
      await updateDoc(itemRef, updateData);
      
      // Update local state
      setCostingItems(prev => prev.map(i => 
        i.id === item.id ? { ...i, ...updateData } : i
      ));
    } catch (error) {
      console.error("Error applying Incoterm rules:", error);
    }
  };

  /**
   * Calculate customs duty for an item
   * Uses HSN code, country of origin, and project country
   */
  const calculateCustomsDuty = async (item) => {
    try {
      const basePrice = parseFloat(item.unitPrice) || 0;
      const quantity = parseFloat(item.quantity) || 0;
      const totalValue = basePrice * quantity;
      
      // For now, use a default duty rate of 10% if HSN code exists
      // In production, this should look up actual duty rates from a table
      if (item.hsnCode && item.hsnCode.trim()) {
        const dutyRate = 0.10; // 10% default
        const dutyAmount = totalValue * dutyRate;
        
        // Also calculate VAT on CIF value + duty
        const cifValue = totalValue;
        const vatRate = 0.15; // 15% VAT
        const vatAmount = (cifValue + dutyAmount) * vatRate;
        
        return dutyAmount + vatAmount;
      }
      
      return 0;
    } catch (error) {
      console.error("Error calculating customs duty:", error);
      return 0;
    }
  };

  /**
   * Calculate freight cost for an item based on weight and dimensions
   * This is called when freight calculator is not used but buyer pays freight
   */
  const calculateFreightCost = (item) => {
    // This would use weight, dimensions, origin, destination
    // For now, return a placeholder
    // In production, this should use actual freight rate tables
    const basePrice = parseFloat(item.unitPrice) || 0;
    const quantity = parseFloat(item.quantity) || 0;
    const totalValue = basePrice * quantity;
    
    // Estimate freight as 5% of total value
    return totalValue * 0.05;
  };

  /**
   * Calculate total landed cost for an item
   * Sum of all cost components based on Incoterm responsibilities
   */
  const calculateTotalLandedCost = (item) => {
    const basePrice = (parseFloat(item.unitPrice) || 0) * (parseFloat(item.quantity) || 0);
    const freightCost = parseFloat(item.freightCost) || 0;
    const insuranceCost = parseFloat(item.insuranceCost) || 0;
    const portCharges = parseFloat(item.portCharges) || 0;
    const customsDutyTax = parseFloat(item.customsDutyTax) || 0;
    const localDelivery = parseFloat(item.localDelivery) || 0;
    
    return basePrice + freightCost + insuranceCost + portCharges + customsDutyTax + localDelivery;
  };

  return (
    <div className={pdfMinimized ? "compliance-container-minimized" : "compliance-container"}>
      {/* PDF Viewer Pane */}
      <div 
        className={pdfMinimized ? "pdf-viewer-pane-minimized" : "pdf-viewer-pane"}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        style={{ userSelect: isDragging ? 'none' : 'text', cursor: isDragging ? 'crosshair' : 'default' }}
      >
        <div className="pdf-header">
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flex: 1 }}>
            <button 
              className="pdf-btn" 
              onClick={() => setPdfMinimized(!pdfMinimized)}
              title={pdfMinimized ? "Show Document" : "Minimize Document"}
              style={{ padding: '6px 12px' }}
            >
              {pdfMinimized ? "📄 Show" : "➖ Minimize"}
            </button>
            {!pdfMinimized && (
              <>
                <select 
                  value={selectedDocUrl} 
                  onChange={(e) => setSelectedDocUrl(e.target.value)} 
                  className="doc-selector"
                  style={{ flex: 1 }}
                >
                  <option value="">Select Document...</option>
                  {tender?.files && tender.files.map((f, i) => (
                    <option key={i} value={f.url}>{f.name}</option>
                  ))}
                </select>
              </>
            )}
          </div>
          {!pdfMinimized && (
            <div style={{ display: 'flex', gap: '5px' }}>
              <button className="pdf-btn" disabled={pageNumber <= 1} onClick={() => handlePageChange(pageNumber - 1)}>
                Prev
              </button>
              <span style={{ fontSize: '12px', alignSelf: 'center', padding: '0 8px' }}>
                Page {pageNumber} of {numPages || '?'}
              </span>
              <button className="pdf-btn" disabled={pageNumber >= numPages} onClick={() => handlePageChange(pageNumber + 1)}>
                Next
              </button>
            </div>
          )}
        </div>

        {!pdfMinimized && (
          <div className="pdf-scroll-area">
            {selectedDocUrl ? (
              <div style={{ position: 'relative' }}>
                <Document 
                  file={selectedDocUrl} 
                  onLoadSuccess={onDocumentLoadSuccess} 
                  onLoadError={onDocumentLoadError}
                  loading={<div style={{ color: 'white', padding: '20px' }}>Loading PDF...</div>}
                >
                  <Page pageNumber={pageNumber} width={PDF_RENDER_WIDTH} />
                </Document>
              </div>
            ) : (
              <div className="pdf-placeholder">
                <p>Select a document to view</p>
                <p style={{ fontSize: '13px', marginTop: '10px' }}>
                  or{' '}
                  <button 
                    className="new-btn" 
                    onClick={() => boqFileInputRef.current.click()}
                    style={{ fontSize: '12px', padding: '4px 12px' }}
                  >
                    Upload BOQ Document
                  </button>
                </p>
              </div>
            )}
          </div>
        )}

        {isDragging && dragRect && (
          <div style={{ 
            position: 'absolute', 
            bottom: '10px', 
            left: '10px',
            background: '#fff',
            padding: '8px 12px',
            borderRadius: '6px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            border: '2px solid #3b82f6',
            fontSize: '12px',
            fontWeight: '600',
            color: '#1e40af'
          }}>
            📦 Release to add item to costing sheet
          </div>
        )}

        <input
          type="file"
          ref={boqFileInputRef}
          onChange={(e) => handleUploadBOQ(e.target.files)}
          accept=".pdf,.xlsx,.xls"
          style={{ display: "none" }}
        />
      </div>

      {/* Costing Table Pane */}
      <div className={pdfMinimized ? "clauses-pane clauses-pane-fullwidth" : "clauses-pane"}>
        {/* Sub-tabs for Costing Sheet */}
        <div style={{ 
          display: 'flex', 
          gap: '0', 
          borderBottom: '2px solid #e2e8f0', 
          marginBottom: '0',
          background: '#f8fafc'
        }}>
          <button 
            onClick={() => setActiveSubTab("costing")}
            style={{ 
              padding: '12px 20px', 
              border: 'none', 
              background: activeSubTab === "costing" ? '#fff' : 'transparent',
              borderBottom: activeSubTab === "costing" ? '2px solid #2563eb' : '2px solid transparent',
              marginBottom: '-2px',
              fontWeight: activeSubTab === "costing" ? '600' : '400',
              color: activeSubTab === "costing" ? '#2563eb' : '#64748b',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            📋 Costing Sheet
          </button>
          <button 
            onClick={() => setActiveSubTab("analysis")}
            style={{ 
              padding: '12px 20px', 
              border: 'none', 
              background: activeSubTab === "analysis" ? '#fff' : 'transparent',
              borderBottom: activeSubTab === "analysis" ? '2px solid #2563eb' : '2px solid transparent',
              marginBottom: '-2px',
              fontWeight: activeSubTab === "analysis" ? '600' : '400',
              color: activeSubTab === "analysis" ? '#2563eb' : '#64748b',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            🔍 AI Analysis
          </button>
          <button 
            onClick={() => setActiveSubTab("calculations")}
            style={{ 
              padding: '12px 20px', 
              border: 'none', 
              background: activeSubTab === "calculations" ? '#fff' : 'transparent',
              borderBottom: activeSubTab === "calculations" ? '2px solid #2563eb' : '2px solid transparent',
              marginBottom: '-2px',
              fontWeight: activeSubTab === "calculations" ? '600' : '400',
              color: activeSubTab === "calculations" ? '#2563eb' : '#64748b',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            🧮 Margin Calculator
          </button>
          <button 
            onClick={() => setActiveSubTab("scenarios")}
            style={{ 
              padding: '12px 20px', 
              border: 'none', 
              background: activeSubTab === "scenarios" ? '#fff' : 'transparent',
              borderBottom: activeSubTab === "scenarios" ? '2px solid #2563eb' : '2px solid transparent',
              marginBottom: '-2px',
              fontWeight: activeSubTab === "scenarios" ? '600' : '400',
              color: activeSubTab === "scenarios" ? '#2563eb' : '#64748b',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            🎯 Scenario Planning
          </button>
        </div>

        {/* Hidden CSV file input */}
        <input
          type="file"
          ref={csvFileInputRef}
          onChange={(e) => handleImportCSV(e.target.files)}
          accept=".csv"
          style={{ display: "none" }}
        />

        {/* ============ COSTING SHEET TAB ============ */}
        {activeSubTab === "costing" && (
          <>
            <div className="clauses-header">
              <h3>Costing Sheet / BOQ</h3>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button 
                  className="new-btn" 
                  onClick={handleExportCSV}
                  style={{ fontSize: '13px', backgroundColor: '#059669', color: 'white' }}
                  title="Download costing sheet as CSV"
                >
                  ⬇️ Export CSV
                </button>
                <button 
                  className="new-btn" 
                  onClick={() => csvFileInputRef.current?.click()}
                  style={{ fontSize: '13px', backgroundColor: '#0891b2', color: 'white' }}
                  title="Upload costing sheet from CSV"
                >
                  ⬆️ Import CSV
                </button>
                <button 
                  className="new-btn common-items-btn" 
                  onClick={handleOpenCommonItems}
                  style={{ fontSize: '13px' }}
                  title="Add common project items"
                >
                  🗂️ Common Items
                </button>
                <button 
                  className="new-btn" 
                  onClick={handleOpenExchangeRateManager}
                  style={{ fontSize: '13px', backgroundColor: '#10b981', color: 'white' }}
                  title="Manage exchange rates"
                >
                  💱 Exchange Rates
                </button>
                <button 
                  className="new-btn ai-btn" 
                  onClick={handleAiExtractBOQ} 
                  disabled={aiLoading || !selectedDocUrl}
                  style={{ fontSize: '13px' }}
                >
                  {aiLoading ? "🤖 Extracting..." : "✨ AI Extract BOQ"}
                </button>
                <button 
                  className="new-btn" 
                  onClick={handleAddRow}
                  style={{ fontSize: '13px' }}
                >
                  + Add Row
                </button>
                <button 
                  className="new-btn" 
                  onClick={handleOpenFreightCalculator}
                  style={{ fontSize: '13px', background: '#3b82f6', color: 'white' }}
                >
                  📦 Calculate Freight
                </button>
                {Object.values(selectedItems).some(v => v) && (
                  <button 
                    className="new-btn vendor-email-btn" 
                    onClick={handleEmailSelectedItems}
                    style={{ fontSize: '13px' }}
                  >
                    📧 Email Selected ({Object.values(selectedItems).filter(v => v).length})
                  </button>
                )}
              </div>
            </div>

            <div style={{ padding: '0 15px 10px', fontSize: '13px', color: '#64748b' }}>
              💡 Click any cell to edit. Drag column borders to resize. Select items with checkboxes and click "📧 Email Selected" to send to vendors. Click 💡 on any item to get AI-suggested accessories.
            </div>

            {loading ? (
              <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                Loading costing items...
              </div>
            ) : (
              <div style={{ 
                overflowX: "auto", 
                overflowY: "auto", 
                maxHeight: pdfMinimized ? "calc(100vh - 250px)" : "calc(100vh - 350px)",
                flex: 1
              }}>
                <table className="costing-table-inline" ref={tableRef} style={{ minWidth: '1200px' }}>
                  <thead>
                    <tr>
                      <th style={{ width: columnWidths.checkbox, minWidth: 40, position: 'sticky', left: 0, background: 'linear-gradient(180deg, #075985, #0369a1)', zIndex: 2 }}>
                        <input 
                          type="checkbox"
                          checked={costingItems.length > 0 && costingItems.every(item => selectedItems[item.id])}
                          onChange={(e) => {
                            const newSelected = {};
                            if (e.target.checked) {
                              costingItems.forEach(item => newSelected[item.id] = true);
                            }
                            setSelectedItems(newSelected);
                          }}
                          title="Select all items"
                          style={{ cursor: 'pointer' }}
                        />
                      </th>
                      <th style={{ width: columnWidths.srno, minWidth: 40 }}>
                        #
                        <span className="resize-handle" onMouseDown={(e) => handleResizeStart(e, 'srno')} />
                      </th>
                      <th style={{ width: columnWidths.item, minWidth: 80 }}>
                        Item
                        <span className="resize-handle" onMouseDown={(e) => handleResizeStart(e, 'item')} />
                      </th>
                      <th style={{ width: columnWidths.description, minWidth: 80 }}>
                        Description
                        <span className="resize-handle" onMouseDown={(e) => handleResizeStart(e, 'description')} />
                      </th>
                      <th style={{ width: columnWidths.subsystem, minWidth: 100 }}>
                        Subsystem
                        <span className="resize-handle" onMouseDown={(e) => handleResizeStart(e, 'subsystem')} />
                      </th>
                      <th style={{ width: columnWidths.qty, minWidth: 50 }}>
                        Qty
                        <span className="resize-handle" onMouseDown={(e) => handleResizeStart(e, 'qty')} />
                      </th>
                      <th style={{ width: columnWidths.unit, minWidth: 50 }}>
                    Unit
                    <span className="resize-handle" onMouseDown={(e) => handleResizeStart(e, 'unit')} />
                  </th>
                  <th style={{ width: columnWidths.make, minWidth: 60 }}>
                    Make
                    <span className="resize-handle" onMouseDown={(e) => handleResizeStart(e, 'make')} />
                  </th>
                  <th style={{ width: columnWidths.model, minWidth: 60 }}>
                    Model
                    <span className="resize-handle" onMouseDown={(e) => handleResizeStart(e, 'model')} />
                  </th>
                  <th style={{ width: columnWidths.remarks, minWidth: 80 }}>
                    Remarks
                    <span className="resize-handle" onMouseDown={(e) => handleResizeStart(e, 'remarks')} />
                  </th>
                  <th style={{ width: columnWidths.hsnCode, minWidth: 80 }}>
                    HSN Code
                    <span className="resize-handle" onMouseDown={(e) => handleResizeStart(e, 'hsnCode')} />
                  </th>
                  <th style={{ width: columnWidths.countryOfOrigin, minWidth: 100 }}>
                    Country of Origin
                    <span className="resize-handle" onMouseDown={(e) => handleResizeStart(e, 'countryOfOrigin')} />
                  </th>
                  <th style={{ width: columnWidths.deliveryTerms, minWidth: 100 }}>
                    Delivery Terms
                    <span className="resize-handle" onMouseDown={(e) => handleResizeStart(e, 'deliveryTerms')} />
                  </th>
                  <th style={{ width: columnWidths.currency, minWidth: 50 }}>
                    Curr
                    <span className="resize-handle" onMouseDown={(e) => handleResizeStart(e, 'currency')} />
                  </th>
                  <th style={{ width: columnWidths.price, minWidth: 70 }}>
                    Price
                    <span className="resize-handle" onMouseDown={(e) => handleResizeStart(e, 'price')} />
                  </th>
                  <th style={{ width: columnWidths.landedCost, minWidth: 70 }} title="Total Landed Cost includes: Base Price (Price × Qty) + Freight + Insurance + Port Charges + Customs Duty/VAT + Local Delivery (based on Incoterm). Click ℹ️ for breakdown.">
                    🌍 Landed Cost
                    <span className="resize-handle" onMouseDown={(e) => handleResizeStart(e, 'landedCost')} />
                  </th>
                  <th style={{ width: columnWidths.actions, minWidth: 40 }}>×</th>
                </tr>
              </thead>
              <tbody>
                {costingItems.length === 0 ? (
                  <tr>
                    <td colSpan="17" style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                      <p>No costing items yet.</p>
                      <p style={{ fontSize: "13px" }}>
                        Use AI to extract BOQ from documents, select text to add items, or click "+ Add Row"
                      </p>
                    </td>
                  </tr>
                ) : (
                  costingItems.map((item, index) => (
                    <tr key={item.id}>
                      <td style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1, textAlign: 'center' }}>
                        <input 
                          type="checkbox"
                          checked={!!selectedItems[item.id]}
                          onChange={(e) => {
                            setSelectedItems(prev => ({
                              ...prev,
                              [item.id]: e.target.checked
                            }));
                          }}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td style={{ background: '#fff' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {index + 1}
                          {canSuggestHSN(item) && (
                            <button
                              className="suggest-btn"
                              onClick={() => handleSuggestAccessories(item.itemName)}
                              disabled={suggestingAccessories}
                              title="Suggest accessories for this item"
                            >
                              💡
                            </button>
                          )}
                        </div>
                      </td>
                      <td>{renderEditableCell(item, "itemName", "100%", "text")}</td>
                      <td>{renderEditableCell(item, "description", "100%", "text")}</td>
                      <td>
                        <select
                          value={item.subsystemId || ""}
                          onChange={async (e) => {
                            const newSubsystemId = e.target.value;
                            try {
                              await updateDoc(doc(db, "tenders", tenderId, "costingItems", item.id), {
                                subsystemId: newSubsystemId
                              });
                              setCostingItems(prev => prev.map(i => 
                                i.id === item.id ? { ...i, subsystemId: newSubsystemId } : i
                              ));
                            } catch (error) {
                              console.error("Error updating subsystem:", error);
                            }
                          }}
                          style={{
                            width: "100%",
                            padding: "4px",
                            border: "1px solid #e2e8f0",
                            borderRadius: "4px",
                            fontSize: "12px",
                            cursor: "pointer"
                          }}
                        >
                          <option value="">None</option>
                          {subsystems.map(subsystem => (
                            <option key={subsystem.id} value={subsystem.id}>
                              {subsystem.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>{renderEditableCell(item, "quantity", "100%", "number")}</td>
                      <td>{renderEditableCell(item, "unit", "100%", "text")}</td>
                      <td>{renderEditableCell(item, "make", "100%", "text")}</td>
                      <td>{renderEditableCell(item, "model", "100%", "text")}</td>
                      <td>{renderEditableCell(item, "remarks", "100%", "text")}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {renderEditableCell(item, "hsnCode", "100%", "text")}
                          {suggestingHSN[item.id] && (
                            <span style={{ fontSize: '10px', color: '#3b82f6' }}>🤖 Auto-filling...</span>
                          )}
                        </div>
                      </td>
                      <td>{renderEditableCell(item, "countryOfOrigin", "100%", "text")}</td>
                      <td>
                        <select
                          value={item.deliveryTerms || ""}
                          onChange={async (e) => {
                            const newDeliveryTerms = e.target.value;
                            try {
                              // Update database first
                              await updateDoc(doc(db, "tenders", tenderId, "costingItems", item.id), {
                                deliveryTerms: newDeliveryTerms
                              });
                              
                              // Update local state only after successful database update
                              const updatedItem = { ...item, deliveryTerms: newDeliveryTerms };
                              setCostingItems(prev => prev.map(i => 
                                i.id === item.id ? updatedItem : i
                              ));
                              
                              // Trigger shipping calculation
                              calculateItemDutyAndShipping(updatedItem);
                              
                              // Apply Incoterm-based cost rules
                              applyIncotermRules(updatedItem);
                            } catch (error) {
                              console.error("Error updating delivery terms:", error);
                              alert("Failed to update delivery terms. Please try again.");
                            }
                          }}
                          style={{
                            width: "100%",
                            padding: "4px",
                            border: "1px solid #e2e8f0",
                            borderRadius: "4px",
                            fontSize: "12px",
                            cursor: "pointer"
                          }}
                        >
                          <option value="">Select...</option>
                          {DELIVERY_TERMS.map(term => (
                            <option key={term} value={term}>
                              {term}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          value={item.currency || defaultCurrency}
                          onChange={async (e) => {
                            const newCurrency = e.target.value;
                            try {
                              await updateDoc(doc(db, "tenders", tenderId, "costingItems", item.id), {
                                currency: newCurrency
                              });
                              setCostingItems(prev => prev.map(i => 
                                i.id === item.id ? { ...i, currency: newCurrency } : i
                              ));
                            } catch (error) {
                              console.error("Error updating currency:", error);
                            }
                          }}
                          style={{
                            width: "100%",
                            padding: "4px",
                            border: "1px solid #e2e8f0",
                            borderRadius: "4px",
                            fontSize: "12px",
                            cursor: "pointer"
                          }}
                        >
                          {SUPPORTED_CURRENCIES.map(curr => (
                            <option key={curr.code} value={curr.code}>
                              {curr.code}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>{renderEditableCell(item, "unitPrice", "100%", "number")}</td>
                      <td style={{ fontWeight: "600", background: "#eff6ff", position: 'relative' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span>{formatCurrencyWithConversion(item.totalLandedCost || item.totalPrice, item.currency || defaultCurrency)}</span>
                          {item.deliveryTerms && getIncotermResponsibilities(item.deliveryTerms) && (
                            <button
                              onClick={() => setcostBreakdownModal({ isOpen: true, item })}
                              style={{ 
                                fontSize: '12px', 
                                cursor: 'pointer', 
                                marginLeft: '4px',
                                background: 'none',
                                border: 'none',
                                padding: '2px 4px',
                                borderRadius: '3px'
                              }}
                              title="Click to see cost breakdown based on Incoterm"
                            >
                              ℹ️
                            </button>
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="delete-x"
                          onClick={() => handleDeleteItem(item.id, item.itemName)}
                          style={{ padding: "4px 8px", fontSize: "14px" }}
                          title="Delete row"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {costingItems.length > 0 && (
                <tfoot>
                  <tr style={{ background: '#f0fdf4', borderTop: '2px solid #34d399' }}>
                    <td colSpan="10" style={{ textAlign: "right", fontWeight: "700", fontSize: "15px", padding: "16px", color: "#065f46" }}>
                      📊 TOTAL LANDED COST (in {defaultCurrency}):
                    </td>
                    <td colSpan="5" style={{ textAlign: "right", fontWeight: "600", fontSize: "13px", padding: "16px", color: "#64748b" }}>
                      {/* Empty space for better visual alignment */}
                    </td>
                    <td colSpan="1" style={{ fontWeight: "700", fontSize: "18px", color: "#0369a1", padding: "16px", background: '#dbeafe', textAlign: "right" }}>
                      {formatCurrency(
                        costingItems.reduce((sum, item) => {
                          const itemCurrency = item.currency || defaultCurrency;
                          const itemTotal = parseFloat(item.totalLandedCost) || parseFloat(item.totalPrice) || 0;
                          const convertedTotal = convertCurrency(itemTotal, itemCurrency, defaultCurrency);
                          return sum + convertedTotal;
                        }, 0),
                        defaultCurrency
                      )}
                    </td>
                    <td colSpan="1"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
          </>
        )}

        {/* ============ AI ANALYSIS TAB ============ */}
        {activeSubTab === "analysis" && (
          <div style={{ padding: '20px' }}>
            <div className="clauses-header">
              <h3>🔍 AI Cost Analysis</h3>
              <button 
                className="new-btn ai-btn" 
                onClick={handleRunAnalysis}
                disabled={isAnalyzing || costingItems.length === 0}
                style={{ fontSize: '13px' }}
              >
                {isAnalyzing ? "🤖 Analyzing..." : "✨ Run AI Analysis"}
              </button>
            </div>

            {costingItems.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                <p>No costing items to analyze.</p>
                <p>Add items in the Costing Sheet tab first.</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '20px' }}>
                {/* Category Breakdown */}
                <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px' }}>
                  <h4 style={{ margin: '0 0 12px 0', color: '#1e293b' }}>📊 Cost Category Breakdown</h4>
                  {analysisResults.categoryBreakdown.length === 0 ? (
                    <p style={{ color: '#64748b', fontSize: '14px' }}>Run analysis to see category breakdown</p>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                      {analysisResults.categoryBreakdown.map(cat => (
                        <div key={cat.id} style={{ 
                          background: 'white', 
                          padding: '12px', 
                          borderRadius: '6px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{ fontSize: '16px', marginBottom: '4px' }}>{cat.icon} {cat.name}</div>
                          <div style={{ fontSize: '20px', fontWeight: '600', color: '#0f172a' }}>
                            {formatCurrency(cat.total, defaultCurrency)}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>{cat.count} item(s)</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Abnormal Prices */}
                {analysisResults.abnormalPrices.length > 0 && (
                  <div style={{ background: '#fef3c7', borderRadius: '8px', padding: '16px', border: '1px solid #fbbf24' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#92400e' }}>⚠️ Abnormal Prices Detected</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {analysisResults.abnormalPrices.map((item, idx) => (
                        <div key={idx} style={{ 
                          background: 'white', 
                          padding: '12px', 
                          borderRadius: '6px',
                          border: '1px solid #fbbf24'
                        }}>
                          <div style={{ fontWeight: '600' }}>{item.itemName}</div>
                          <div style={{ fontSize: '14px', color: '#92400e' }}>{item.reason}</div>
                          <div style={{ fontSize: '13px', color: '#64748b' }}>
                            Current Price: {formatCurrency(item.unitPrice, item.currency || defaultCurrency)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Hidden Charges */}
                {analysisResults.hiddenCharges.length > 0 && (
                  <div style={{ background: '#fee2e2', borderRadius: '8px', padding: '16px', border: '1px solid #f87171' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#b91c1c' }}>🚨 Potential Hidden Charges</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {analysisResults.hiddenCharges.map((item, idx) => (
                        <div key={idx} style={{ 
                          background: 'white', 
                          padding: '12px', 
                          borderRadius: '6px',
                          border: '1px solid #f87171'
                        }}>
                          <div style={{ fontWeight: '600' }}>{item.itemName}</div>
                          <div style={{ fontSize: '13px', color: '#64748b' }}>{item.description}</div>
                          <div style={{ fontSize: '14px', color: '#b91c1c' }}>
                            {formatCurrency(item.totalPrice, item.currency || defaultCurrency)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Detected Mistakes */}
                {analysisResults.mistakes.length > 0 && (
                  <div style={{ background: '#fef2f2', borderRadius: '8px', padding: '16px', border: '1px solid #fca5a5' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#dc2626' }}>❌ Potential Mistakes Detected</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {analysisResults.mistakes.map((mistake, idx) => (
                        <div key={idx} style={{ 
                          background: 'white', 
                          padding: '12px', 
                          borderRadius: '6px',
                          border: `1px solid ${mistake.severity === 'high' ? '#f87171' : mistake.severity === 'medium' ? '#fbbf24' : '#94a3b8'}`
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            marginBottom: '4px'
                          }}>
                            <span style={{ 
                              fontSize: '11px', 
                              fontWeight: '600', 
                              padding: '2px 6px', 
                              borderRadius: '4px',
                              background: mistake.severity === 'high' ? '#fef2f2' : mistake.severity === 'medium' ? '#fffbeb' : '#f8fafc',
                              color: mistake.severity === 'high' ? '#dc2626' : mistake.severity === 'medium' ? '#d97706' : '#64748b'
                            }}>
                              {mistake.severity.toUpperCase()}
                            </span>
                            <span style={{ fontWeight: '600' }}>{mistake.message}</span>
                          </div>
                          {mistake.items && (
                            <div style={{ fontSize: '12px', color: '#64748b' }}>
                              Affected items: {mistake.items.map(i => i.itemName).slice(0, 3).join(', ')}
                              {mistake.items.length > 3 && ` and ${mistake.items.length - 3} more`}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Suggestions */}
                {analysisResults.suggestions.length > 0 && (
                  <div style={{ background: '#ecfdf5', borderRadius: '8px', padding: '16px', border: '1px solid #34d399' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#065f46' }}>💡 Suggestions</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {analysisResults.suggestions.map((suggestion, idx) => (
                        <div key={idx} style={{ 
                          background: 'white', 
                          padding: '12px', 
                          borderRadius: '6px',
                          border: '1px solid #34d399'
                        }}>
                          <div style={{ fontWeight: '600', marginBottom: '4px' }}>{suggestion.message}</div>
                          <div style={{ fontSize: '13px', color: '#065f46' }}>
                            📋 Action: {suggestion.action}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ============ MARGIN CALCULATOR TAB ============ */}
        {activeSubTab === "calculations" && (
          <div style={{ padding: '20px', maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
            <div className="clauses-header">
              <h3>🧮 Auto-Generate Margin & Cost Calculations</h3>
            </div>

            {/* AI Calculation Info Banner */}
            <div style={{ 
              background: '#eff6ff', 
              border: '1px solid #3b82f6', 
              borderRadius: '6px', 
              padding: '12px 16px', 
              marginBottom: '12px',
              fontSize: '13px',
              color: '#1e40af'
            }}>
              <strong>🤖 AI-Powered Risk Analysis:</strong> Use the AI buttons to get intelligent suggestions for Currency Volatility Buffer (based on 90-day volatility), 
              Contingency (based on project complexity), and Risk (based on supply chain analysis). Adjust the factors manually or let AI guide your margin calculations.
            </div>

            {/* Risk Clarification Banner */}
            <div style={{ 
              background: '#fef3c7', 
              border: '1px solid #fbbf24', 
              borderRadius: '6px', 
              padding: '10px 14px', 
              marginBottom: '20px',
              fontSize: '12px',
              color: '#92400e'
            }}>
              <strong>💡 Quick Guide:</strong> <span style={{ fontWeight: '600' }}>Contingency</span> = known risks (complex items, tight timeline). 
              <span style={{ fontWeight: '600', marginLeft: '8px' }}>Risk Buffer</span> = unknown unknowns (supply chain disruptions, unforeseen issues).
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              {/* Configuration Panel */}
              <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px' }}>
                <h4 style={{ margin: '0 0 16px 0', color: '#1e293b' }}>⚙️ Risk & Margin Factors (%)</h4>
                
                <div style={{ display: 'grid', gap: '12px' }}>
                  {/* Lead Time Risk Factor */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ fontSize: '14px' }}>⏰ Lead Time Risk Factor</label>
                    <input
                      type="number"
                      value={calculationRates.leadTimeRisk}
                      onChange={(e) => updateCalculationRate('leadTimeRisk', e.target.value)}
                      style={{ width: '80px', padding: '6px', borderRadius: '4px', border: '1px solid #e2e8f0', textAlign: 'right' }}
                    />
                  </div>

                  {/* Currency Volatility Buffer with AI */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                    <label style={{ fontSize: '14px', flex: 1 }}>💱 Currency Volatility Buffer</label>
                    <input
                      type="number"
                      value={calculationRates.currencyVolatility}
                      onChange={(e) => updateCalculationRate('currencyVolatility', e.target.value)}
                      style={{ width: '80px', padding: '6px', borderRadius: '4px', border: '1px solid #e2e8f0', textAlign: 'right' }}
                    />
                    <button
                      onClick={handleCalculateCurrencyVolatility}
                      disabled={calculatingVolatility}
                      style={{
                        padding: '6px 10px',
                        fontSize: '11px',
                        background: calculatingVolatility ? '#e5e7eb' : '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: calculatingVolatility ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                      title="AI suggests based on 90-day currency volatility"
                    >
                      {calculatingVolatility ? '...' : '🤖 AI'}
                    </button>
                  </div>

                  {/* Warranty Handling Cost */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ fontSize: '14px' }}>🛠️ Warranty Handling Cost</label>
                    <input
                      type="number"
                      value={calculationRates.warrantyHandling}
                      onChange={(e) => updateCalculationRate('warrantyHandling', e.target.value)}
                      style={{ width: '80px', padding: '6px', borderRadius: '4px', border: '1px solid #e2e8f0', textAlign: 'right' }}
                    />
                  </div>

                  {/* Contingency with AI */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                    <label style={{ fontSize: '14px', flex: 1 }} title="Known risks: complexity, timeline, custom items">⚠️ Contingency (Known Risks)</label>
                    <input
                      type="number"
                      value={calculationRates.contingency}
                      onChange={(e) => updateCalculationRate('contingency', e.target.value)}
                      style={{ width: '80px', padding: '6px', borderRadius: '4px', border: '1px solid #e2e8f0', textAlign: 'right' }}
                    />
                    <button
                      onClick={handleCalculateContingency}
                      disabled={calculatingContingency}
                      style={{
                        padding: '6px 10px',
                        fontSize: '11px',
                        background: calculatingContingency ? '#e5e7eb' : '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: calculatingContingency ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                      title="AI suggests based on project complexity and known risks"
                    >
                      {calculatingContingency ? '...' : '🤖 AI'}
                    </button>
                  </div>

                  {/* Risk with AI */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                    <label style={{ fontSize: '14px', flex: 1 }} title="Unknown unknowns: supply chain, geopolitical, unforeseen issues">🎲 Risk Buffer (Unknown Unknowns)</label>
                    <input
                      type="number"
                      value={calculationRates.risk}
                      onChange={(e) => updateCalculationRate('risk', e.target.value)}
                      style={{ width: '80px', padding: '6px', borderRadius: '4px', border: '1px solid #e2e8f0', textAlign: 'right' }}
                    />
                    <button
                      onClick={handleCalculateRisk}
                      disabled={calculatingRisk}
                      style={{
                        padding: '6px 10px',
                        fontSize: '11px',
                        background: calculatingRisk ? '#e5e7eb' : '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: calculatingRisk ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                      title="AI suggests based on supply chain risks and unforeseen factors"
                    >
                      {calculatingRisk ? '...' : '🤖 AI'}
                    </button>
                  </div>

                  {/* Business Agent */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ fontSize: '14px' }}>👔 Business Agent Commission</label>
                    <input
                      type="number"
                      value={calculationRates.businessAgent}
                      onChange={(e) => updateCalculationRate('businessAgent', e.target.value)}
                      style={{ width: '80px', padding: '6px', borderRadius: '4px', border: '1px solid #e2e8f0', textAlign: 'right' }}
                    />
                  </div>

                  {/* COGS */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ fontSize: '14px' }}>📦 COGS (Cost of Goods Sold)</label>
                    <input
                      type="number"
                      value={calculationRates.cogs}
                      onChange={(e) => updateCalculationRate('cogs', e.target.value)}
                      style={{ width: '80px', padding: '6px', borderRadius: '4px', border: '1px solid #e2e8f0', textAlign: 'right' }}
                    />
                  </div>

                  {/* Margin */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '2px solid #e2e8f0', paddingTop: '12px' }}>
                    <label style={{ fontSize: '14px', fontWeight: '600' }}>📈 Margin</label>
                    <input
                      type="number"
                      value={calculationRates.margin}
                      onChange={(e) => updateCalculationRate('margin', e.target.value)}
                      style={{ width: '80px', padding: '6px', borderRadius: '4px', border: '1px solid #e2e8f0', textAlign: 'right' }}
                    />
                  </div>
                </div>
              </div>

              {/* Results Panel */}
              <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '16px', border: '1px solid #34d399' }}>
                <h4 style={{ margin: '0 0 16px 0', color: '#065f46' }}>📊 Cost Breakdown</h4>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {/* Base Cost */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', background: 'white', borderRadius: '4px' }}>
                    <span>📦 Base Cost (Materials)</span>
                    <span style={{ fontWeight: '600' }}>{formatCurrency(calculatedCosts.baseCost, defaultCurrency)}</span>
                  </div>

                  {/* Risk Factors */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', background: 'white', borderRadius: '4px' }}>
                    <span>⏰ Lead Time Risk ({calculationRates.leadTimeRisk}%)</span>
                    <span>{formatCurrency(calculatedCosts.leadTimeRisk, defaultCurrency)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', background: 'white', borderRadius: '4px' }}>
                    <span>💱 Currency Volatility ({calculationRates.currencyVolatility}%)</span>
                    <span>{formatCurrency(calculatedCosts.currencyVolatility, defaultCurrency)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', background: 'white', borderRadius: '4px' }}>
                    <span>🛠️ Warranty Handling ({calculationRates.warrantyHandling}%)</span>
                    <span>{formatCurrency(calculatedCosts.warrantyHandling, defaultCurrency)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', background: 'white', borderRadius: '4px' }}>
                    <span>⚠️ Contingency ({calculationRates.contingency}%)</span>
                    <span>{formatCurrency(calculatedCosts.contingency, defaultCurrency)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', background: 'white', borderRadius: '4px' }}>
                    <span>🎲 Risk ({calculationRates.risk}%)</span>
                    <span>{formatCurrency(calculatedCosts.risk, defaultCurrency)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', background: 'white', borderRadius: '4px' }}>
                    <span>👔 Business Agent ({calculationRates.businessAgent}%)</span>
                    <span>{formatCurrency(calculatedCosts.businessAgent, defaultCurrency)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', background: 'white', borderRadius: '4px' }}>
                    <span>📦 COGS ({calculationRates.cogs}%)</span>
                    <span>{formatCurrency(calculatedCosts.cogs, defaultCurrency)}</span>
                  </div>
                  
                  {/* Total Cost before margin */}
                  <div style={{ borderTop: '2px solid #34d399', paddingTop: '10px', marginTop: '5px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', background: '#dcfce7', borderRadius: '4px' }}>
                      <span style={{ fontWeight: '600' }}>💰 Total Cost (before margin)</span>
                      <span style={{ fontWeight: '600' }}>{formatCurrency(calculatedCosts.totalCost, defaultCurrency)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', background: 'white', borderRadius: '4px', marginTop: '6px' }}>
                      <span>📈 Margin ({calculationRates.margin}%)</span>
                      <span style={{ color: '#059669' }}>{formatCurrency(calculatedCosts.margin, defaultCurrency)}</span>
                    </div>
                  </div>
                  
                  {/* Total Cost to Bid */}
                  <div style={{ borderTop: '2px solid #065f46', paddingTop: '10px', marginTop: '5px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', background: '#065f46', borderRadius: '6px', color: 'white' }}>
                      <span style={{ fontWeight: '700', fontSize: '16px' }}>🏷️ Total Cost of Project to Bid</span>
                      <span style={{ fontWeight: '700', fontSize: '18px' }}>{formatCurrency(calculatedCosts.totalCostToBid, defaultCurrency)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Discount Impact */}
            <div style={{ marginTop: '20px', background: '#f0f9ff', borderRadius: '8px', padding: '16px', border: '1px solid #0ea5e9' }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#0369a1' }}>🏷️ Customer Discount Impact Analysis</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px' }}>
                {[5, 10, 15, 20, 25].map(discount => {
                  const discountedPrice = calculatedCosts.totalCostToBid * (1 - discount / 100);
                  const newMargin = discountedPrice - calculatedCosts.totalCost;
                  const marginPercent = calculatedCosts.totalCost > 0 ? ((newMargin / calculatedCosts.totalCost) * 100).toFixed(1) : 0;
                  return (
                    <div key={discount} style={{ 
                      background: 'white', 
                      padding: '12px', 
                      borderRadius: '6px',
                      border: '1px solid #0ea5e9',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#0369a1' }}>{discount}% Discount</div>
                      <div style={{ fontSize: '14px', marginTop: '8px' }}>
                        Price: {formatCurrency(discountedPrice, defaultCurrency)}
                      </div>
                      <div style={{ 
                        fontSize: '13px', 
                        marginTop: '4px',
                        color: newMargin >= 0 ? '#059669' : '#dc2626'
                      }}>
                        Margin: {marginPercent}%
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Action Recommendation Box */}
            <div style={{ marginTop: '20px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '12px', padding: '24px', border: '2px solid #5a67d8', boxShadow: '0 10px 25px rgba(102, 126, 234, 0.3)' }}>
              <h4 style={{ margin: '0 0 20px 0', color: 'white', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '24px' }}>💡</span>
                <span>Action Recommendations</span>
              </h4>
              
              {(() => {
                // Calculate key pricing metrics
                const recommendedSellingPrice = calculatedCosts.totalCostToBid; // With full margin
                const minimumSellingPrice = calculatedCosts.totalCost * 1.05; // 5% minimum margin
                const breakEvenPrice = calculatedCosts.totalCost; // No margin
                const targetMarginPercent = calculationRates.margin;
                const minimumMarginPercent = 5;
                
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                    {/* Recommended Selling Price */}
                    <div style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '8px', padding: '16px', border: '2px solid rgba(255, 255, 255, 0.3)' }}>
                      <div style={{ fontSize: '12px', color: '#667eea', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        ✅ Recommended Selling Price
                      </div>
                      <div style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
                        {formatCurrency(recommendedSellingPrice, defaultCurrency)}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        With {targetMarginPercent}% margin
                      </div>
                      <div style={{ fontSize: '11px', color: '#059669', marginTop: '8px', fontWeight: '600' }}>
                        💰 Profit: {formatCurrency(calculatedCosts.margin, defaultCurrency)}
                      </div>
                    </div>

                    {/* Minimum Selling Price */}
                    <div style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '8px', padding: '16px', border: '2px solid rgba(255, 255, 255, 0.3)' }}>
                      <div style={{ fontSize: '12px', color: '#f59e0b', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        ⚠️ Minimum Selling Price
                      </div>
                      <div style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
                        {formatCurrency(minimumSellingPrice, defaultCurrency)}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        With {minimumMarginPercent}% margin (floor)
                      </div>
                      <div style={{ fontSize: '11px', color: '#f59e0b', marginTop: '8px', fontWeight: '600' }}>
                        ⚡ Minimum profit to accept
                      </div>
                    </div>

                    {/* Break-even Price */}
                    <div style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '8px', padding: '16px', border: '2px solid rgba(255, 255, 255, 0.3)' }}>
                      <div style={{ fontSize: '12px', color: '#dc2626', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        🚫 Break-even Price
                      </div>
                      <div style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
                        {formatCurrency(breakEvenPrice, defaultCurrency)}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        Zero margin (don't go below!)
                      </div>
                      <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '8px', fontWeight: '600' }}>
                        ❌ No profit - reject below this
                      </div>
                    </div>
                  </div>
                );
              })()}
              
              {/* Quick Action Summary */}
              <div style={{ marginTop: '16px', background: 'rgba(255, 255, 255, 0.15)', borderRadius: '8px', padding: '12px', backdropFilter: 'blur(10px)' }}>
                <div style={{ color: 'white', fontSize: '13px', fontWeight: '500', marginBottom: '8px' }}>
                  📋 Quick Summary for Management:
                </div>
                <div style={{ color: 'rgba(255, 255, 255, 0.95)', fontSize: '12px', lineHeight: '1.6' }}>
                  "Quote at <strong>{formatCurrency(calculatedCosts.totalCostToBid, defaultCurrency)}</strong> with <strong>{calculationRates.margin}% margin</strong>. 
                  Can negotiate down to <strong>{formatCurrency(calculatedCosts.totalCost * 1.05, defaultCurrency)}</strong> (5% margin) if needed. 
                  Never go below <strong>{formatCurrency(calculatedCosts.totalCost, defaultCurrency)}</strong> (break-even)."
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============ SCENARIO PLANNING TAB ============ */}
        {activeSubTab === "scenarios" && (
          <div style={{ padding: '20px', maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
            <div className="clauses-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>🎯 Scenario Planning - Interactive Margin Simulator</h3>
              <button 
                onClick={generateScenarioPDF}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
              >
                <span style={{ fontSize: '16px' }}>📄</span>
                <span>Export Snapshot PDF</span>
              </button>
            </div>

            <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '20px' }}>
              Simulate different procurement scenarios and visualize their impact on your margins. Adjust factors and see how your profitability changes in real-time.
            </p>

            {/* Predefined Scenarios with Visualizations */}
            <div style={{ marginBottom: '30px' }}>
              <h4 style={{ margin: '0 0 16px 0', color: '#1e293b' }}>📊 Predefined Scenarios</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px' }}>
                
                {/* Scenario A: Worst-Case Procurement */}
                {(() => {
                  const worstCaseFactors = {
                    leadTimeRisk: 10,
                    currencyVolatility: 10,
                    warrantyHandling: 5,
                    contingency: 15,
                    risk: 12,
                    businessAgent: calculationRates.businessAgent,
                    cogs: calculationRates.cogs,
                    margin: calculationRates.margin
                  };
                  const worstCaseBase = calculatedCosts.baseCost;
                  const worstCaseCosts = worstCaseBase * (1 + (worstCaseFactors.leadTimeRisk + worstCaseFactors.currencyVolatility + worstCaseFactors.warrantyHandling + worstCaseFactors.contingency + worstCaseFactors.risk + worstCaseFactors.businessAgent + worstCaseFactors.cogs) / 100);
                  const worstCaseMargin = worstCaseCosts * (worstCaseFactors.margin / 100);
                  const worstCaseTotalCost = worstCaseCosts + worstCaseMargin;
                  const worstCaseMarginPercent = ((worstCaseMargin / worstCaseCosts) * 100).toFixed(1);
                  
                  return (
                    <div style={{ background: '#fef2f2', borderRadius: '8px', padding: '16px', border: '2px solid #fca5a5' }}>
                      <h5 style={{ margin: '0 0 12px 0', color: '#dc2626', fontSize: '16px' }}>🔴 Scenario A: Worst-Case Procurement</h5>
                      <p style={{ fontSize: '12px', color: '#991b1b', marginBottom: '12px' }}>High freight, delayed lead time, unfavorable exchange, maximum contingency</p>
                      
                      {/* Factors */}
                      <div style={{ background: 'white', borderRadius: '6px', padding: '12px', marginBottom: '12px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px', fontSize: '13px' }}>
                          <span>🚚 Lead Time Risk:</span><span style={{ fontWeight: '600', color: '#dc2626' }}>{worstCaseFactors.leadTimeRisk}%</span>
                          <span>💱 Currency Volatility:</span><span style={{ fontWeight: '600', color: '#dc2626' }}>{worstCaseFactors.currencyVolatility}%</span>
                          <span>🛠️ Warranty:</span><span style={{ fontWeight: '600', color: '#dc2626' }}>{worstCaseFactors.warrantyHandling}%</span>
                          <span>⚠️ Contingency:</span><span style={{ fontWeight: '600', color: '#dc2626' }}>{worstCaseFactors.contingency}%</span>
                          <span>🎲 Risk Buffer:</span><span style={{ fontWeight: '600', color: '#dc2626' }}>{worstCaseFactors.risk}%</span>
                        </div>
                      </div>
                      
                      {/* Results */}
                      <div style={{ background: 'white', borderRadius: '6px', padding: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ fontSize: '13px' }}>Total Cost:</span>
                          <span style={{ fontSize: '16px', fontWeight: '700' }}>{formatCurrency(worstCaseTotalCost, defaultCurrency)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '13px' }}>Margin:</span>
                          <span style={{ fontSize: '14px', fontWeight: '600', color: '#dc2626' }}>{worstCaseMarginPercent}% ({formatCurrency(worstCaseMargin, defaultCurrency)})</span>
                        </div>
                      </div>
                      
                      {/* Visual Bar */}
                      <div style={{ marginTop: '12px' }}>
                        <div style={{ fontSize: '11px', color: '#991b1b', marginBottom: '4px' }}>Cost vs Margin Breakdown:</div>
                        <div style={{ display: 'flex', height: '30px', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{ width: `${(worstCaseCosts / worstCaseTotalCost) * 100}%`, background: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '11px', fontWeight: '600' }}>
                            Cost {((worstCaseCosts / worstCaseTotalCost) * 100).toFixed(0)}%
                          </div>
                          <div style={{ width: `${(worstCaseMargin / worstCaseTotalCost) * 100}%`, background: '#fca5a5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7f1d1d', fontSize: '11px', fontWeight: '600' }}>
                            Margin {((worstCaseMargin / worstCaseTotalCost) * 100).toFixed(0)}%
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Scenario B: Best Vendor Outcome */}
                {(() => {
                  const bestCaseFactors = {
                    leadTimeRisk: 2,
                    currencyVolatility: 2,
                    warrantyHandling: 1,
                    contingency: 3,
                    risk: 2,
                    businessAgent: calculationRates.businessAgent,
                    cogs: calculationRates.cogs,
                    margin: calculationRates.margin
                  };
                  const bestCaseBase = calculatedCosts.baseCost;
                  const bestCaseCosts = bestCaseBase * (1 + (bestCaseFactors.leadTimeRisk + bestCaseFactors.currencyVolatility + bestCaseFactors.warrantyHandling + bestCaseFactors.contingency + bestCaseFactors.risk + bestCaseFactors.businessAgent + bestCaseFactors.cogs) / 100);
                  const bestCaseMargin = bestCaseCosts * (bestCaseFactors.margin / 100);
                  const bestCaseTotalCost = bestCaseCosts + bestCaseMargin;
                  const bestCaseMarginPercent = ((bestCaseMargin / bestCaseCosts) * 100).toFixed(1);
                  
                  return (
                    <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '16px', border: '2px solid #34d399' }}>
                      <h5 style={{ margin: '0 0 12px 0', color: '#059669', fontSize: '16px' }}>🟢 Scenario B: Best Vendor Outcome</h5>
                      <p style={{ fontSize: '12px', color: '#065f46', marginBottom: '12px' }}>Best price quote, fastest delivery, lowest duty, minimal risk buffer</p>
                      
                      {/* Factors */}
                      <div style={{ background: 'white', borderRadius: '6px', padding: '12px', marginBottom: '12px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px', fontSize: '13px' }}>
                          <span>🚚 Lead Time Risk:</span><span style={{ fontWeight: '600', color: '#059669' }}>{bestCaseFactors.leadTimeRisk}%</span>
                          <span>💱 Currency Volatility:</span><span style={{ fontWeight: '600', color: '#059669' }}>{bestCaseFactors.currencyVolatility}%</span>
                          <span>🛠️ Warranty:</span><span style={{ fontWeight: '600', color: '#059669' }}>{bestCaseFactors.warrantyHandling}%</span>
                          <span>⚠️ Contingency:</span><span style={{ fontWeight: '600', color: '#059669' }}>{bestCaseFactors.contingency}%</span>
                          <span>🎲 Risk Buffer:</span><span style={{ fontWeight: '600', color: '#059669' }}>{bestCaseFactors.risk}%</span>
                        </div>
                      </div>
                      
                      {/* Results */}
                      <div style={{ background: 'white', borderRadius: '6px', padding: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ fontSize: '13px' }}>Total Cost:</span>
                          <span style={{ fontSize: '16px', fontWeight: '700' }}>{formatCurrency(bestCaseTotalCost, defaultCurrency)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '13px' }}>Margin:</span>
                          <span style={{ fontSize: '14px', fontWeight: '600', color: '#059669' }}>{bestCaseMarginPercent}% ({formatCurrency(bestCaseMargin, defaultCurrency)})</span>
                        </div>
                      </div>
                      
                      {/* Visual Bar */}
                      <div style={{ marginTop: '12px' }}>
                        <div style={{ fontSize: '11px', color: '#065f46', marginBottom: '4px' }}>Cost vs Margin Breakdown:</div>
                        <div style={{ display: 'flex', height: '30px', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{ width: `${(bestCaseCosts / bestCaseTotalCost) * 100}%`, background: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '11px', fontWeight: '600' }}>
                            Cost {((bestCaseCosts / bestCaseTotalCost) * 100).toFixed(0)}%
                          </div>
                          <div style={{ width: `${(bestCaseMargin / bestCaseTotalCost) * 100}%`, background: '#34d399', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#065f46', fontSize: '11px', fontWeight: '600' }}>
                            Margin {((bestCaseMargin / bestCaseTotalCost) * 100).toFixed(0)}%
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Scenario C: Internal Approval */}
                {(() => {
                  const approvalFactors = {
                    leadTimeRisk: 5,
                    currencyVolatility: 5,
                    warrantyHandling: 3,
                    contingency: 5,
                    risk: 5,
                    businessAgent: calculationRates.businessAgent,
                    cogs: calculationRates.cogs,
                    margin: 22 // Fixed margin for internal approval
                  };
                  const approvalBase = calculatedCosts.baseCost;
                  const approvalCosts = approvalBase * (1 + (approvalFactors.leadTimeRisk + approvalFactors.currencyVolatility + approvalFactors.warrantyHandling + approvalFactors.contingency + approvalFactors.risk + approvalFactors.businessAgent + approvalFactors.cogs) / 100);
                  const approvalMargin = approvalCosts * (approvalFactors.margin / 100);
                  const approvalTotalCost = approvalCosts + approvalMargin;
                  const approvalMarginPercent = ((approvalMargin / approvalCosts) * 100).toFixed(1);
                  
                  return (
                    <div style={{ background: '#eff6ff', borderRadius: '8px', padding: '16px', border: '2px solid #3b82f6' }}>
                      <h5 style={{ margin: '0 0 12px 0', color: '#1e40af', fontSize: '16px' }}>🔵 Scenario C: Internal Approval</h5>
                      <p style={{ fontSize: '12px', color: '#1e3a8a', marginBottom: '12px' }}>Standard margins: 22% margin, 5% risk buffer, standard overheads</p>
                      
                      {/* Factors */}
                      <div style={{ background: 'white', borderRadius: '6px', padding: '12px', marginBottom: '12px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px', fontSize: '13px' }}>
                          <span>🚚 Lead Time Risk:</span><span style={{ fontWeight: '600', color: '#1e40af' }}>{approvalFactors.leadTimeRisk}%</span>
                          <span>💱 Currency Volatility:</span><span style={{ fontWeight: '600', color: '#1e40af' }}>{approvalFactors.currencyVolatility}%</span>
                          <span>🛠️ Warranty:</span><span style={{ fontWeight: '600', color: '#1e40af' }}>{approvalFactors.warrantyHandling}%</span>
                          <span>⚠️ Contingency:</span><span style={{ fontWeight: '600', color: '#1e40af' }}>{approvalFactors.contingency}%</span>
                          <span>🎲 Risk Buffer:</span><span style={{ fontWeight: '600', color: '#1e40af' }}>{approvalFactors.risk}%</span>
                          <span style={{ borderTop: '1px solid #e2e8f0', paddingTop: '4px' }}>📈 Margin (Fixed):</span><span style={{ fontWeight: '700', color: '#1e40af', borderTop: '1px solid #e2e8f0', paddingTop: '4px' }}>{approvalFactors.margin}%</span>
                        </div>
                      </div>
                      
                      {/* Results */}
                      <div style={{ background: 'white', borderRadius: '6px', padding: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ fontSize: '13px' }}>Total Cost:</span>
                          <span style={{ fontSize: '16px', fontWeight: '700' }}>{formatCurrency(approvalTotalCost, defaultCurrency)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '13px' }}>Margin:</span>
                          <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e40af' }}>{approvalMarginPercent}% ({formatCurrency(approvalMargin, defaultCurrency)})</span>
                        </div>
                      </div>
                      
                      {/* Visual Bar */}
                      <div style={{ marginTop: '12px' }}>
                        <div style={{ fontSize: '11px', color: '#1e3a8a', marginBottom: '4px' }}>Cost vs Margin Breakdown:</div>
                        <div style={{ display: 'flex', height: '30px', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{ width: `${(approvalCosts / approvalTotalCost) * 100}%`, background: '#1e40af', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '11px', fontWeight: '600' }}>
                            Cost {((approvalCosts / approvalTotalCost) * 100).toFixed(0)}%
                          </div>
                          <div style={{ width: `${(approvalMargin / approvalTotalCost) * 100}%`, background: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '11px', fontWeight: '600' }}>
                            Margin {((approvalMargin / approvalTotalCost) * 100).toFixed(0)}%
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Current Scenario Comparison */}
            <div style={{ marginBottom: '30px', background: '#fafafa', borderRadius: '8px', padding: '16px', border: '1px solid #e5e5e5' }}>
              <h4 style={{ margin: '0 0 16px 0', color: '#1e293b' }}>📈 Your Current Scenario</h4>
              <div style={{ background: 'white', borderRadius: '6px', padding: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>Current Factors</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '6px', fontSize: '13px' }}>
                      <span>⏰ Lead Time Risk:</span><span style={{ fontWeight: '600' }}>{calculationRates.leadTimeRisk}%</span>
                      <span>💱 Currency Volatility:</span><span style={{ fontWeight: '600' }}>{calculationRates.currencyVolatility}%</span>
                      <span>🛠️ Warranty:</span><span style={{ fontWeight: '600' }}>{calculationRates.warrantyHandling}%</span>
                      <span>⚠️ Contingency:</span><span style={{ fontWeight: '600' }}>{calculationRates.contingency}%</span>
                      <span>🎲 Risk:</span><span style={{ fontWeight: '600' }}>{calculationRates.risk}%</span>
                      <span style={{ borderTop: '1px solid #e2e8f0', paddingTop: '4px' }}>📈 Margin:</span><span style={{ fontWeight: '700', borderTop: '1px solid #e2e8f0', paddingTop: '4px' }}>{calculationRates.margin}%</span>
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>Results</div>
                    <div style={{ background: '#f8fafc', borderRadius: '6px', padding: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ fontSize: '13px' }}>Base Cost:</span>
                        <span style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(calculatedCosts.baseCost, defaultCurrency)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ fontSize: '13px' }}>Total Cost:</span>
                        <span style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(calculatedCosts.totalCost, defaultCurrency)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                        <span style={{ fontSize: '13px' }}>Margin:</span>
                        <span style={{ fontSize: '14px', fontWeight: '700', color: '#059669' }}>{formatCurrency(calculatedCosts.margin, defaultCurrency)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', background: '#065f46', color: 'white', padding: '8px', borderRadius: '4px' }}>
                        <span style={{ fontSize: '14px', fontWeight: '600' }}>Total to Bid:</span>
                        <span style={{ fontSize: '16px', fontWeight: '700' }}>{formatCurrency(calculatedCosts.totalCostToBid, defaultCurrency)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Visual Bar for Current */}
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>Cost vs Margin Breakdown:</div>
                  {calculatedCosts.totalCostToBid > 0 ? (
                    <div style={{ display: 'flex', height: '40px', borderRadius: '6px', overflow: 'hidden', border: '2px solid #e5e5e5' }}>
                      <div style={{ width: `${(calculatedCosts.totalCost / calculatedCosts.totalCostToBid) * 100}%`, background: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '12px', fontWeight: '600' }}>
                        Total Cost {((calculatedCosts.totalCost / calculatedCosts.totalCostToBid) * 100).toFixed(1)}%
                      </div>
                      <div style={{ width: `${(calculatedCosts.margin / calculatedCosts.totalCostToBid) * 100}%`, background: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '12px', fontWeight: '600' }}>
                        Margin {((calculatedCosts.margin / calculatedCosts.totalCostToBid) * 100).toFixed(1)}%
                      </div>
                    </div>
                  ) : (
                    <div style={{ padding: '10px', background: '#f8fafc', borderRadius: '6px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
                      No cost data available
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Scenario Comparison Chart */}
            <div style={{ marginBottom: '30px' }}>
              <h4 style={{ margin: '0 0 16px 0', color: '#1e293b' }}>📊 Scenario Comparison Chart</h4>
              <div style={{ background: 'white', borderRadius: '8px', padding: '20px', border: '1px solid #e2e8f0' }}>
                {(() => {
                  const scenarios = [
                    { name: 'Worst Case', color: '#dc2626', factors: { leadTimeRisk: 10, currencyVolatility: 10, warrantyHandling: 5, contingency: 15, risk: 12, businessAgent: calculationRates.businessAgent, cogs: calculationRates.cogs, margin: calculationRates.margin } },
                    { name: 'Current', color: '#475569', factors: calculationRates },
                    { name: 'Best Case', color: '#059669', factors: { leadTimeRisk: 2, currencyVolatility: 2, warrantyHandling: 1, contingency: 3, risk: 2, businessAgent: calculationRates.businessAgent, cogs: calculationRates.cogs, margin: calculationRates.margin } },
                    { name: 'Approval (22%)', color: '#1e40af', factors: { leadTimeRisk: 5, currencyVolatility: 5, warrantyHandling: 3, contingency: 5, risk: 5, businessAgent: calculationRates.businessAgent, cogs: calculationRates.cogs, margin: 22 } }
                  ];
                  
                  const scenarioData = scenarios.map(s => {
                    const base = calculatedCosts.baseCost;
                    const costs = base * (1 + (s.factors.leadTimeRisk + s.factors.currencyVolatility + s.factors.warrantyHandling + s.factors.contingency + s.factors.risk + (s.factors.businessAgent || 0) + (s.factors.cogs || 0)) / 100);
                    const margin = costs * (s.factors.margin / 100);
                    const total = costs + margin;
                    return { ...s, costs, margin, total };
                  });
                  
                  const maxTotal = Math.max(...scenarioData.map(s => s.total), 1); // Ensure at least 1 to avoid division by zero
                  
                  return (
                    <div>
                      {maxTotal > 0 ? (
                        <div style={{ marginBottom: '20px' }}>
                          {scenarioData.map((s, idx) => (
                            <div key={idx} style={{ marginBottom: '16px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px' }}>
                                <span style={{ fontWeight: '600', color: s.color }}>{s.name}</span>
                                <span style={{ fontWeight: '600' }}>{formatCurrency(s.total, defaultCurrency)}</span>
                              </div>
                              {s.total > 0 ? (
                                <>
                                  <div style={{ display: 'flex', height: '32px', borderRadius: '4px', overflow: 'hidden', width: `${Math.max((s.total / maxTotal) * 100, 5)}%`, border: '1px solid #e2e8f0' }}>
                                    <div 
                                      style={{ 
                                        width: `${(s.costs / s.total) * 100}%`, 
                                        background: s.color, 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        color: 'white', 
                                        fontSize: '11px', 
                                        fontWeight: '600' 
                                      }}
                                      title={`Cost: ${formatCurrency(s.costs, defaultCurrency)}`}
                                    >
                                      {((s.costs / s.total) * 100).toFixed(0)}%
                                    </div>
                                    <div 
                                      style={{ 
                                        width: `${(s.margin / s.total) * 100}%`, 
                                        background: `${s.color}80`, 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        fontSize: '11px', 
                                        fontWeight: '600',
                                        color: s.color
                                      }}
                                      title={`Margin: ${formatCurrency(s.margin, defaultCurrency)}`}
                                    >
                                      {((s.margin / s.total) * 100).toFixed(0)}%
                                    </div>
                                  </div>
                                  <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                                    Cost: {formatCurrency(s.costs, defaultCurrency)} | Margin: {formatCurrency(s.margin, defaultCurrency)} ({s.factors.margin}%)
                                  </div>
                                </>
                              ) : (
                                <div style={{ padding: '8px', background: '#f8fafc', borderRadius: '4px', fontSize: '12px', color: '#64748b' }}>
                                  No data available
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ padding: '20px', background: '#f8fafc', borderRadius: '6px', textAlign: 'center', color: '#64748b' }}>
                          No scenario data available. Add items to your costing sheet to see comparisons.
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Vendor Discount Simulator */}
            <div style={{ marginBottom: '30px' }}>
              <h4 style={{ margin: '0 0 16px 0', color: '#1e293b' }}>💰 Vendor Discount Impact Simulator</h4>
              <div style={{ background: 'white', borderRadius: '8px', padding: '20px', border: '1px solid #e2e8f0' }}>
                <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                  See how securing vendor discounts affects your margin. The table shows how your margin increases with different discount levels.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
                  {[5, 10, 15, 20].map(discount => {
                    const discountedBase = calculatedCosts.baseCost * (1 - discount / 100);
                    const discountedCosts = discountedBase * (1 + (calculationRates.leadTimeRisk + calculationRates.currencyVolatility + calculationRates.warrantyHandling + calculationRates.contingency + calculationRates.risk + calculationRates.businessAgent + calculationRates.cogs) / 100);
                    const discountedMargin = discountedCosts * (calculationRates.margin / 100);
                    const discountedTotal = discountedCosts + discountedMargin;
                    const savings = calculatedCosts.totalCostToBid - discountedTotal;
                    const marginIncrease = discountedMargin - calculatedCosts.margin;
                    
                    return (
                      <div key={discount} style={{ background: '#f0fdf4', borderRadius: '6px', padding: '14px', border: '2px solid #34d399' }}>
                        <div style={{ fontSize: '18px', fontWeight: '700', color: '#059669', marginBottom: '8px' }}>
                          {discount}% Discount
                        </div>
                        <div style={{ fontSize: '12px', color: '#065f46', marginBottom: '6px' }}>
                          New Total: <strong>{formatCurrency(discountedTotal, defaultCurrency)}</strong>
                        </div>
                        <div style={{ fontSize: '12px', color: '#065f46', marginBottom: '6px' }}>
                          Savings: <strong>{formatCurrency(savings, defaultCurrency)}</strong>
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#059669', borderTop: '1px solid #34d399', paddingTop: '6px', marginTop: '6px' }}>
                          Margin Boost: +{formatCurrency(marginIncrease, defaultCurrency)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Scenario Modal */}
      {scenarioModal.isOpen && (
        <div className="accessory-modal-overlay" onClick={handleCloseScenarioModal}>
          <div className="accessory-modal" onClick={(e) => e.stopPropagation()}>
            <div className="accessory-modal-header">
              <h3>🎯 Create New Scenario</h3>
              <button className="accessory-modal-close" onClick={handleCloseScenarioModal}>×</button>
            </div>
            
            <div className="accessory-modal-body">
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Scenario Name</label>
                <input
                  type="text"
                  value={scenarioModal.name}
                  onChange={(e) => setScenarioModal(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Alternative Supplier Pricing"
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Description (optional)</label>
                <textarea
                  value={scenarioModal.description}
                  onChange={(e) => setScenarioModal(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="What changes are you exploring in this scenario?"
                  rows="3"
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                />
              </div>
            </div>
            
            <div className="accessory-modal-footer">
              <div className="accessory-modal-actions">
                <button className="cancel-btn" onClick={handleCloseScenarioModal}>
                  Cancel
                </button>
                <button 
                  className="new-btn" 
                  onClick={handleSaveScenario}
                >
                  Create Scenario
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Accessory Suggestion Modal */}
      {accessoryModal.isOpen && (
        <div className="accessory-modal-overlay" onClick={handleCloseAccessoryModal}>
          <div className="accessory-modal" onClick={(e) => e.stopPropagation()}>
            <div className="accessory-modal-header">
              <h3>💡 Suggested Accessories for "{accessoryModal.itemName}"</h3>
              <button className="accessory-modal-close" onClick={handleCloseAccessoryModal}>×</button>
            </div>
            
            <div className="accessory-modal-body">
              <p style={{ marginTop: 0, marginBottom: '16px', color: '#64748b', fontSize: '14px' }}>
                Select the accessories you want to add to your costing sheet:
              </p>
              
              {accessoryModal.accessories.map((acc, idx) => (
                <div 
                  key={idx} 
                  className={`accessory-item ${accessoryModal.selected[idx] ? 'selected' : ''}`}
                  onClick={() => handleToggleAccessory(idx)}
                >
                  <input
                    type="checkbox"
                    className="accessory-checkbox"
                    checked={accessoryModal.selected[idx] || false}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggleAccessory(idx);
                    }}
                  />
                  <div className="accessory-details">
                    <div className="accessory-name">{acc.itemName}</div>
                    <div className="accessory-desc">{acc.description}</div>
                    <div className="accessory-meta">
                      Quantity: {acc.quantity} {acc.unit}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="accessory-modal-footer">
              <label className="accessory-select-all">
                <input
                  type="checkbox"
                  checked={Object.values(accessoryModal.selected).every(v => v)}
                  onChange={(e) => handleSelectAllAccessories(e.target.checked)}
                />
                Select All ({accessoryModal.accessories.length})
              </label>
              
              <div className="accessory-modal-actions">
                <button className="cancel-btn" onClick={handleCloseAccessoryModal}>
                  Cancel
                </button>
                <button 
                  className="new-btn" 
                  onClick={handleAddSelectedAccessories}
                  disabled={!Object.values(accessoryModal.selected).some(v => v)}
                >
                  Add Selected ({Object.values(accessoryModal.selected).filter(v => v).length})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Common Items Modal */}
      {commonItemsModal.isOpen && (
        <div className="accessory-modal-overlay" onClick={handleCloseCommonItems}>
          <div className="accessory-modal" onClick={(e) => e.stopPropagation()}>
            <div className="accessory-modal-header">
              <h3>🗂️ Add Common Project Items</h3>
              <button className="accessory-modal-close" onClick={handleCloseCommonItems}>×</button>
            </div>
            
            <div className="accessory-modal-body">
              <p style={{ marginTop: 0, marginBottom: '16px', color: '#64748b', fontSize: '14px' }}>
                Select commonly used project items to add to your BOQ:
              </p>
              
              {COMMON_ITEMS.map((itemName, idx) => (
                <div 
                  key={idx} 
                  className={`accessory-item ${commonItemsModal.selected[idx] ? 'selected' : ''}`}
                  onClick={() => handleToggleCommonItem(idx)}
                >
                  <input
                    type="checkbox"
                    className="accessory-checkbox"
                    checked={commonItemsModal.selected[idx] || false}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggleCommonItem(idx);
                    }}
                  />
                  <div className="accessory-details">
                    <div className="accessory-name">{itemName}</div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="accessory-modal-footer">
              <label className="accessory-select-all">
                <input
                  type="checkbox"
                  checked={Object.values(commonItemsModal.selected).every(v => v)}
                  onChange={(e) => handleSelectAllCommonItems(e.target.checked)}
                />
                Select All ({COMMON_ITEMS.length})
              </label>
              
              <div className="accessory-modal-actions">
                <button className="cancel-btn" onClick={handleCloseCommonItems}>
                  Cancel
                </button>
                <button 
                  className="new-btn" 
                  onClick={handleAddCommonItems}
                  disabled={!Object.values(commonItemsModal.selected).some(v => v)}
                >
                  Add Selected ({Object.values(commonItemsModal.selected).filter(v => v).length})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Exchange Rate Manager Modal */}
      {exchangeRateModal.isOpen && (
        <div className="accessory-modal-overlay" onClick={handleCloseExchangeRateManager}>
          <div className="accessory-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="accessory-modal-header">
              <h3>💱 Exchange Rate Manager</h3>
              <button className="accessory-modal-close" onClick={handleCloseExchangeRateManager}>×</button>
            </div>
            
            <div className="accessory-modal-body">
              <p style={{ marginTop: 0, marginBottom: '16px', color: '#64748b', fontSize: '14px' }}>
                Set custom exchange rates. Format: 1 unit of currency = X USD
                {Object.keys(customRates).length > 0 && (
                  <span style={{ display: 'block', marginTop: '8px', color: '#10b981', fontWeight: '500' }}>
                    ✓ Using custom rates
                  </span>
                )}
              </p>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {SUPPORTED_CURRENCIES.map((currency) => (
                  <div 
                    key={currency.code} 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      padding: '8px',
                      backgroundColor: currency.code === 'USD' ? '#f1f5f9' : 'transparent',
                      borderRadius: '4px'
                    }}
                  >
                    <label 
                      style={{ 
                        flex: '0 0 100px', 
                        fontWeight: '500',
                        fontSize: '13px'
                      }}
                    >
                      {currency.symbol} {currency.code}
                    </label>
                    <input
                      type="number"
                      step="0.0001"
                      min="0"
                      disabled={currency.code === 'USD'}
                      value={exchangeRateModal.rates[currency.code] || 1.0}
                      onChange={(e) => handleUpdateExchangeRate(currency.code, e.target.value)}
                      style={{
                        flex: '1',
                        padding: '6px 8px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontFamily: 'monospace',
                        backgroundColor: currency.code === 'USD' ? '#f8fafc' : 'white'
                      }}
                    />
                  </div>
                ))}
              </div>
              
              <div style={{ 
                marginTop: '16px', 
                padding: '12px', 
                backgroundColor: '#fef3c7', 
                borderRadius: '4px',
                fontSize: '12px',
                color: '#92400e'
              }}>
                💡 <strong>Tip:</strong> USD is the base currency (always 1.0). 
                Enter how much 1 unit of each currency equals in USD. 
                Example: If 1 EUR = 1.08 USD, enter 1.08 for EUR.
              </div>
            </div>
            
            <div className="accessory-modal-footer">
              <button 
                className="cancel-btn" 
                onClick={handleResetExchangeRates}
                style={{ marginRight: 'auto', backgroundColor: '#ef4444', color: 'white' }}
                title="Reset to default static rates"
              >
                🔄 Reset to Default
              </button>
              
              <div className="accessory-modal-actions">
                <button className="cancel-btn" onClick={handleCloseExchangeRateManager}>
                  Cancel
                </button>
                <button 
                  className="new-btn" 
                  onClick={handleSaveExchangeRates}
                  style={{ backgroundColor: '#10b981' }}
                >
                  💾 Save Rates
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Calculate Freight Modal */}
      {freightCalculatorModal.isOpen && (
        <div className="accessory-modal-overlay" onClick={handleCloseFreightCalculator}>
          <div className="accessory-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="accessory-modal-header">
              <h3>📦 Calculate Freight Costs</h3>
              <button className="accessory-modal-close" onClick={handleCloseFreightCalculator}>×</button>
            </div>
            
            <div className="accessory-modal-body">
              <div style={{ 
                background: '#eff6ff', 
                padding: '10px 12px', 
                borderRadius: '6px', 
                marginBottom: '16px',
                fontSize: '12px',
                color: '#1e40af',
                border: '1px solid #3b82f6'
              }}>
                <strong>ℹ️ Note:</strong> Calculated freight will be added as a <strong>new line item</strong> in the costing sheet.
                Only items where YOU are responsible for freight (based on Incoterm) are shown here.
              </div>
              
              {(() => {
                const eligibleItems = getEligibleFreightItems();
                
                if (eligibleItems.length === 0) {
                  return (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                      <p style={{ fontSize: '16px', marginBottom: '8px' }}>No eligible items for freight calculation</p>
                      <p style={{ fontSize: '13px', margin: 0 }}>
                        Items must have: Make, Model, and an Incoterm where YOU pay freight (EXW, FCA, FAS, FOB, CFR, CPT).
                        For DDP, DAP, CIP, CIF, DPU - vendor covers freight.
                      </p>
                    </div>
                  );
                }
                
                const selectedCount = Object.values(freightCalculatorModal.selectedItems).filter(v => v).length;
                
                return (
                  <>
                    {/* Item Selection */}
                    <div style={{ marginBottom: '20px' }}>
                      <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>
                        Select Items to Group ({selectedCount} selected)
                      </h4>
                      <div style={{ 
                        maxHeight: '200px', 
                        overflowY: 'auto', 
                        border: '1px solid #e2e8f0', 
                        borderRadius: '4px',
                        padding: '8px'
                      }}>
                        {eligibleItems.map(item => (
                          <label 
                            key={item.id}
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              padding: '8px',
                              background: freightCalculatorModal.selectedItems[item.id] ? '#eff6ff' : 'transparent',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              marginBottom: '4px'
                            }}
                          >
                            <input 
                              type="checkbox"
                              checked={freightCalculatorModal.selectedItems[item.id] || false}
                              onChange={() => handleToggleFreightItem(item.id)}
                              style={{ marginRight: '8px' }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '13px', fontWeight: '500', color: '#1e293b' }}>
                                {item.item}
                              </div>
                              <div style={{ fontSize: '12px', color: '#64748b' }}>
                                {item.make} {item.model} • Qty: {item.quantity} • {item.deliveryTerms}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    {/* Packaging Specifications */}
                    {selectedCount > 0 && (
                      <>
                        <div style={{ marginBottom: '20px' }}>
                          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>
                            Packaging Specifications
                          </h4>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                            <div>
                              <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                                Items per Carton *
                              </label>
                              <input 
                                type="number"
                                min="1"
                                value={freightCalculatorModal.packaging.itemsPerCarton}
                                onChange={(e) => handleFreightPackagingChange('itemsPerCarton', e.target.value)}
                                style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                                placeholder="e.g., 10"
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                                Length (cm) *
                              </label>
                              <input 
                                type="number"
                                min="0"
                                step="0.1"
                                value={freightCalculatorModal.packaging.length}
                                onChange={(e) => handleFreightPackagingChange('length', e.target.value)}
                                style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                                placeholder="e.g., 60"
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                                Width (cm) *
                              </label>
                              <input 
                                type="number"
                                min="0"
                                step="0.1"
                                value={freightCalculatorModal.packaging.width}
                                onChange={(e) => handleFreightPackagingChange('width', e.target.value)}
                                style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                                placeholder="e.g., 40"
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                                Height (cm) *
                              </label>
                              <input 
                                type="number"
                                min="0"
                                step="0.1"
                                value={freightCalculatorModal.packaging.height}
                                onChange={(e) => handleFreightPackagingChange('height', e.target.value)}
                                style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                                placeholder="e.g., 35"
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                                Weight (kg) *
                              </label>
                              <input 
                                type="number"
                                min="0"
                                step="0.1"
                                value={freightCalculatorModal.packaging.weight}
                                onChange={(e) => handleFreightPackagingChange('weight', e.target.value)}
                                style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                                placeholder="e.g., 12"
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                                Freight Mode *
                              </label>
                              <select 
                                value={freightCalculatorModal.packaging.freightMode}
                                onChange={(e) => handleFreightPackagingChange('freightMode', e.target.value)}
                                style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                              >
                                <option value="Sea">🚢 Sea Freight</option>
                                <option value="Air">✈️ Air Freight</option>
                              </select>
                            </div>
                          </div>
                        </div>
                        
                        {/* Calculate Button */}
                        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                          <button 
                            onClick={handleCalculateFreight}
                            disabled={freightCalculatorModal.calculating}
                            style={{ 
                              flex: 1,
                              background: '#10b981',
                              color: 'white',
                              border: 'none',
                              padding: '12px 24px',
                              borderRadius: '6px',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: freightCalculatorModal.calculating ? 'not-allowed' : 'pointer',
                              opacity: freightCalculatorModal.calculating ? 0.6 : 1
                            }}
                          >
                            {freightCalculatorModal.calculating ? '⏳ Calculating...' : '🧮 Calculate Freight'}
                          </button>
                        </div>
                        
                        {/* Results */}
                        {freightCalculatorModal.results && (
                          <div style={{ 
                            background: '#f0fdf4', 
                            border: '1px solid #86efac', 
                            borderRadius: '6px', 
                            padding: '16px',
                            marginBottom: '16px'
                          }}>
                            <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#065f46', marginBottom: '12px' }}>
                              📊 Freight Calculation Results
                            </h4>
                            <div style={{ fontSize: '13px', color: '#065f46', marginBottom: '8px' }}>
                              <strong>Number of Cartons:</strong> {freightCalculatorModal.results.numCartons}
                            </div>
                            <div style={{ fontSize: '13px', color: '#065f46', marginBottom: '8px' }}>
                              <strong>Calculation:</strong> {freightCalculatorModal.results.calculation}
                            </div>
                            <div style={{ fontSize: '16px', fontWeight: '600', color: '#065f46', marginTop: '12px' }}>
                              <strong>Total Freight Cost:</strong> {formatCurrency(freightCalculatorModal.results.freightCostItemCurrency, freightCalculatorModal.results.currency)}
                              <span style={{ fontSize: '13px', fontWeight: '400', marginLeft: '8px' }}>
                                (${freightCalculatorModal.results.freightCostUSD.toFixed(2)} USD)
                              </span>
                            </div>
                            
                            <button 
                              onClick={handleAddFreightToCosting}
                              style={{ 
                                background: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                padding: '10px 20px',
                                borderRadius: '4px',
                                fontSize: '13px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                marginTop: '12px'
                              }}
                            >
                              ➕ Add to Costing Sheet
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </>
                );
              })()}
            </div>
            
            <div className="accessory-modal-footer">
              <div></div>
              <div className="accessory-modal-actions">
                <button className="cancel-btn" onClick={handleCloseFreightCalculator}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cost Breakdown Modal - Shows Incoterm responsibilities */}
      {costBreakdownModal.isOpen && costBreakdownModal.item && (
        <div className="accessory-modal-overlay" onClick={() => setcostBreakdownModal({ isOpen: false, item: null })}>
          <div className="accessory-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="accessory-modal-header">
              <h3>💰 Cost Breakdown - {costBreakdownModal.item.itemName}</h3>
              <button className="accessory-modal-close" onClick={() => setcostBreakdownModal({ isOpen: false, item: null })}>×</button>
            </div>
            
            <div className="accessory-modal-body">
              {(() => {
                const item = costBreakdownModal.item;
                const responsibilities = getIncotermResponsibilities(item.deliveryTerms);
                const incoterm = extractIncotermCode(item.deliveryTerms);
                
                if (!responsibilities) {
                  return <p>No Incoterm selected for this item.</p>;
                }
                
                const basePrice = (parseFloat(item.unitPrice) || 0) * (parseFloat(item.quantity) || 0);
                
                return (
                  <div>
                    <div style={{ 
                      background: '#eff6ff', 
                      padding: '12px', 
                      borderRadius: '6px', 
                      marginBottom: '16px',
                      border: '1px solid #3b82f6'
                    }}>
                      <strong style={{ color: '#1e40af' }}>Incoterm: {incoterm}</strong>
                      <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#1e40af' }}>
                        {responsibilities.description}
                      </p>
                    </div>
                    
                    <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>Cost Breakdown:</h4>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {/* Base Price */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        padding: '10px', 
                        background: '#f8fafc', 
                        borderRadius: '4px',
                        border: '1px solid #e2e8f0'
                      }}>
                        <span>📦 Base Price ({item.quantity} × {formatCurrency(item.unitPrice || 0, item.currency)})</span>
                        <strong>{formatCurrency(basePrice, item.currency)}</strong>
                      </div>
                      
                      {/* Freight */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '10px', 
                        background: responsibilities.freight === 'vendor' ? '#dcfce7' : '#fef3c7', 
                        borderRadius: '4px',
                        border: `1px solid ${responsibilities.freight === 'vendor' ? '#86efac' : '#fcd34d'}`
                      }}>
                        <div>
                          <span>🚚 Freight Cost</span>
                          <div style={{ fontSize: '11px', marginTop: '2px', color: '#64748b' }}>
                            {responsibilities.freight === 'vendor' ? '✓ Vendor covers' : '❌ You pay'}
                          </div>
                        </div>
                        <strong>{formatCurrency(item.freightCost || 0, item.currency)}</strong>
                      </div>
                      
                      {/* Insurance */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '10px', 
                        background: responsibilities.insurance === 'vendor' ? '#dcfce7' : '#fef3c7', 
                        borderRadius: '4px',
                        border: `1px solid ${responsibilities.insurance === 'vendor' ? '#86efac' : '#fcd34d'}`
                      }}>
                        <div>
                          <span>🛡️ Insurance</span>
                          <div style={{ fontSize: '11px', marginTop: '2px', color: '#64748b' }}>
                            {responsibilities.insurance === 'vendor' ? '✓ Vendor covers' : '❌ You pay'}
                          </div>
                        </div>
                        <strong>{formatCurrency(item.insuranceCost || 0, item.currency)}</strong>
                      </div>
                      
                      {/* Port Charges */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '10px', 
                        background: responsibilities.portCharges === 'vendor' ? '#dcfce7' : '#fef3c7', 
                        borderRadius: '4px',
                        border: `1px solid ${responsibilities.portCharges === 'vendor' ? '#86efac' : '#fcd34d'}`
                      }}>
                        <div>
                          <span>⚓ Port Charges</span>
                          <div style={{ fontSize: '11px', marginTop: '2px', color: '#64748b' }}>
                            {responsibilities.portCharges === 'vendor' ? '✓ Vendor covers' : '❌ You pay'}
                          </div>
                        </div>
                        <strong>{formatCurrency(item.portCharges || 0, item.currency)}</strong>
                      </div>
                      
                      {/* Customs Duty & VAT */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '10px', 
                        background: responsibilities.customsDuty === 'vendor' ? '#dcfce7' : '#fef3c7', 
                        borderRadius: '4px',
                        border: `1px solid ${responsibilities.customsDuty === 'vendor' ? '#86efac' : '#fcd34d'}`
                      }}>
                        <div>
                          <span>🏛️ Customs Duty & VAT</span>
                          <div style={{ fontSize: '11px', marginTop: '2px', color: '#64748b' }}>
                            {responsibilities.customsDuty === 'vendor' ? '✓ Vendor covers' : '❌ You pay'}
                          </div>
                        </div>
                        <strong>{formatCurrency(item.customsDutyTax || 0, item.currency)}</strong>
                      </div>
                      
                      {/* Local Delivery */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '10px', 
                        background: responsibilities.localDelivery === 'vendor' ? '#dcfce7' : '#fef3c7', 
                        borderRadius: '4px',
                        border: `1px solid ${responsibilities.localDelivery === 'vendor' ? '#86efac' : '#fcd34d'}`
                      }}>
                        <div>
                          <span>🚛 Local Delivery</span>
                          <div style={{ fontSize: '11px', marginTop: '2px', color: '#64748b' }}>
                            {responsibilities.localDelivery === 'vendor' ? '✓ Vendor covers' : '❌ You pay'}
                          </div>
                        </div>
                        <strong>{formatCurrency(item.localDelivery || 0, item.currency)}</strong>
                      </div>
                      
                      {/* Total Landed Cost */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        padding: '12px', 
                        background: '#1e40af', 
                        borderRadius: '6px',
                        color: 'white',
                        marginTop: '8px'
                      }}>
                        <strong>🌍 Total Landed Cost</strong>
                        <strong style={{ fontSize: '16px' }}>{formatCurrency(item.totalLandedCost || basePrice, item.currency)}</strong>
                      </div>
                    </div>
                    
                    <div style={{ 
                      marginTop: '16px', 
                      padding: '12px', 
                      background: '#f0fdf4', 
                      borderRadius: '6px',
                      fontSize: '12px',
                      color: '#065f46',
                      border: '1px solid #86efac'
                    }}>
                      <strong>💡 Legend:</strong><br/>
                      <span style={{ color: '#16a34a' }}>✓ Green = Vendor pays (included in their price)</span><br/>
                      <span style={{ color: '#ca8a04' }}>❌ Yellow = You pay (added to your cost)</span>
                    </div>
                  </div>
                );
              })()}
            </div>
            
            <div className="accessory-modal-footer">
              <div></div>
              <div className="accessory-modal-actions">
                <button className="new-btn" onClick={() => setcostBreakdownModal({ isOpen: false, item: null })}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Freight Details Modal */}
    </div>
  );
}
